import { Document, Page, Text, View, StyleSheet, Image, Svg, Path, Line, Polyline, Circle, G } from "@react-pdf/renderer";
import {
  TESTES_CF, referenciaCF, formatarFaixaCF, classificarCF, CORES_CLASSIFICACAO_CF,
} from '../data/tabelasCapacidadeFuncional';
import { PERGUNTAS_ANAMNESE } from '../data/perguntasAnamnese';
import figuraHomem from '../images/Anatomia-homem-b.png';
import figuraMulher from '../images/Anatomia-mulher-b.png';

import { textoClassificacaoPreensao, descreverAssimetria, OBSERVACAO_PREENSAO_MANUAL, PROTOCOLO_PREENSAO_MANUAL } from '../utils/preensaoManual';
// ---------- Helpers ----------

// R9: campo vazio renderiza como travessão (não null/undefined/NaN/0 enganoso).
const v = (val) => {
  if (val === undefined || val === null) return '—';
  if (typeof val === 'string' && val.trim() === '') return '—';
  if (Array.isArray(val)) return val.length === 0 ? '—' : val.join(', ');
  if (typeof val === 'object') return '—';
  if (typeof val === 'number' && Number.isNaN(val)) return '—';
  return String(val);
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const formatDateTime = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  return `${formatDate(d.toISOString())} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// Sexo: exibe capitalizado ("masculino" salvo no cadastro → "Masculino").
const formatSexo = (s) => {
  if (s === undefined || s === null || String(s).trim() === '') return '—';
  const t = String(s).trim().toLowerCase();
  if (t === 'masculino' || t === 'm') return 'Masculino';
  if (t === 'feminino' || t === 'f') return 'Feminino';
  return t.charAt(0).toUpperCase() + t.slice(1);
};

// Anamnese/PAR-Q são salvos como array [{ id, valor }]. Normaliza (suporta formato legado em objeto).
const normalizarRespostas = (dados) => {
  if (Array.isArray(dados)) {
    return dados.filter((x) => x && typeof x === 'object' && 'id' in x);
  }
  if (dados && typeof dados === 'object') {
    return Object.entries(dados)
      .filter(([k]) => k !== 'lastUpdateDate' && k !== 'evaluationDate')
      .map(([k, valor]) => ({ id: Number(k), valor }));
  }
  return [];
};

// Resposta legível: arrays viram lista por vírgula; objetos/vazios viram travessão.
const formatResposta = (valor) => {
  if (Array.isArray(valor)) {
    const itens = valor.map((x) => String(x).trim()).filter(Boolean);
    return itens.length ? itens.join(', ') : '—';
  }
  if (valor === undefined || valor === null) return '—';
  if (typeof valor === 'object') return '—';
  const s = String(valor).trim();
  return s === '' ? '—' : s;
};

// As respostas guardam só o id da pergunta; o enunciado vem de data/perguntasAnamnese.js (todos os perfis).
const LABELS_ANAMNESE = PERGUNTAS_ANAMNESE;

const LABELS_PARQ = [
  '1. Algum médico já disse que você tem problema no coração e que só deve fazer atividade física com supervisão profissional?',
  '2. Você sente dores no peito quando pratica atividade física?',
  '3. No último mês, você sentiu dor no peito ao praticar atividade física?',
  '4. Você apresenta desequilíbrio devido à tontura e/ou perda de consciência?',
  '5. Você possui algum problema ósseo ou articular que poderia ser piorado pela atividade física?',
  '6. Você toma atualmente algum medicamento para pressão arterial e/ou problema de coração?',
  '7. Sabe de alguma outra razão pela qual você não deve praticar atividade física?',
];

// Índice da faixa de um valor. Valores abaixo da primeira ou acima da última faixa caem nelas
// (a régua já posicionava o marcador assim; a classificação ficava vazia), e pequenas lacunas
// entre intervalos (ex.: 18.4 → 18.5) são resolvidas pela faixa seguinte.
const indiceDaFaixa = (valor, faixas) => {
  const n = Number(valor);
  if (Number.isNaN(n) || !Array.isArray(faixas) || faixas.length === 0) return -1;
  const validas = faixas.filter((f) => Array.isArray(f.range));
  if (validas.length === 0) return -1;
  let i = faixas.findIndex((f) => Array.isArray(f.range) && n >= f.range[0] && n <= f.range[1]);
  if (i >= 0) return i;
  if (n < validas[0].range[0]) return faixas.indexOf(validas[0]);
  if (n > validas[validas.length - 1].range[1]) return faixas.indexOf(validas[validas.length - 1]);
  i = faixas.findIndex((f) => Array.isArray(f.range) && n <= f.range[1]);
  return i >= 0 ? i : faixas.length - 1;
};

// Classifica um valor numérico dentro de faixas [{ name, range: [min, max] }].
const classificar = (valor, faixas) => {
  const i = indiceDaFaixa(valor, faixas);
  return i >= 0 ? faixas[i].name : null;
};

const ehMasculino = (sexo) => String(sexo || '').trim().toLowerCase().startsWith('m');

// --- Faixas de classificação (espelham os formulários; não são persistidas) ---
const FAIXAS_GORDURA_H = [
  { name: 'Abaixo do normal', range: [0, 12] }, { name: 'Normal', range: [12, 18] },
  { name: 'Acima do normal', range: [18, 25] }, { name: 'Tendência à obesidade', range: [25, 100] },
];
const FAIXAS_GORDURA_M = [
  { name: 'Abaixo do normal', range: [0, 16] }, { name: 'Normal', range: [16, 25] },
  { name: 'Acima do normal', range: [25, 33] }, { name: 'Tendência à obesidade', range: [33, 100] },
];
const FAIXAS_FFMI_H = [
  { name: 'Muito baixo', range: [0, 16.99], laudo: 'O avaliado apresenta índice de massa magra abaixo do esperado para sua altura, sugerindo necessidade de estratégias voltadas ao aumento ou preservação de massa muscular.', treino: 'Foco em força básica, adaptação neuromuscular, progressão leve de volume, exercícios multiarticulares e acompanhamento nutricional.' },
  { name: 'Abaixo da média', range: [17, 18.49], laudo: 'O avaliado apresenta quantidade de massa magra abaixo da média, com boa margem para evolução por meio de treinamento de força e suporte nutricional adequado.', treino: 'Foco em hipertrofia inicial, progressão de cargas e aumento gradual do volume semanal.' },
  { name: 'Média', range: [18.5, 20.49], laudo: 'O avaliado apresenta índice de massa magra dentro da média esperada para sua estatura.', treino: 'Recomendado trabalho de recomposição corporal ou hipertrofia progressiva, conforme objetivo.' },
  { name: 'Bom', range: [20.5, 22.49], laudo: 'O avaliado apresenta boa quantidade de massa magra para sua altura, indicando base muscular consistente e composição corporal favorável.', treino: 'Manutenção com progressão, hipertrofia direcionada e refinamento da prescrição conforme objetivo.' },
  { name: 'Excelente', range: [22.5, 24.99], laudo: 'O avaliado apresenta excelente nível de massa magra para sua estrutura corporal, compatível com indivíduos bem treinados.', treino: 'Estratégia voltada para manutenção, hipertrofia específica ou performance.' },
  { name: 'Muito alto', range: [25, 99], laudo: 'O avaliado apresenta índice de massa magra muito elevado, compatível com indivíduos altamente treinados.', treino: 'Controle fino de carga, performance, manutenção e monitoramento periódico.' },
];
const FAIXAS_FFMI_M = [
  { name: 'Muito baixo', range: [0, 13.99], laudo: 'A avaliada apresenta índice de massa magra abaixo do esperado para sua altura, sugerindo necessidade de estratégias voltadas ao aumento ou preservação de massa muscular.', treino: 'Foco em força básica, adaptação neuromuscular, progressão leve de volume, exercícios multiarticulares e acompanhamento nutricional.' },
  { name: 'Abaixo da média', range: [14, 15.49], laudo: 'A avaliada apresenta quantidade de massa magra abaixo da média, com boa margem para evolução por meio de treinamento de força e suporte nutricional adequado.', treino: 'Foco em hipertrofia inicial, progressão de cargas, organização de rotina de treino e ênfase em constância.' },
  { name: 'Média', range: [15.5, 16.99], laudo: 'A avaliada apresenta índice de massa magra dentro da média esperada para sua estatura.', treino: 'Recomendado trabalho de recomposição corporal ou hipertrofia progressiva, conforme objetivo.' },
  { name: 'Bom', range: [17, 18.49], laudo: 'A avaliada apresenta boa quantidade de massa magra para sua altura, indicando base muscular consistente e composição corporal favorável.', treino: 'Manutenção com progressão, hipertrofia direcionada e refinamento da prescrição conforme objetivo.' },
  { name: 'Excelente', range: [18.5, 19.99], laudo: 'A avaliada apresenta excelente nível de massa magra para sua estrutura corporal, compatível com indivíduos bem treinados.', treino: 'Estratégia voltada para manutenção, hipertrofia específica ou performance.' },
  { name: 'Muito alto', range: [20, 99], laudo: 'A avaliada apresenta índice de massa magra muito elevado, compatível com indivíduos altamente treinados.', treino: 'Controle fino de carga, performance, manutenção e monitoramento periódico.' },
];
const FAIXAS_IMC = [
  { name: 'Abaixo do peso', range: [0, 18.4] }, { name: 'Peso normal', range: [18.5, 24.9] },
  { name: 'Sobrepeso', range: [25, 29.9] }, { name: 'Obesidade I', range: [30, 34.9] },
  { name: 'Obesidade II', range: [35, 39.9] }, { name: 'Obesidade III', range: [40, 80] },
];
const FAIXAS_ICA = [
  { name: 'Adiposidade central saudável', range: [0.05, 0.5] },
  { name: 'Adiposidade central elevada', range: [0.5, 0.6] },
  { name: 'Adiposidade central alta', range: [0.6, 1.8] },
];
const FAIXAS_ABDOMEN = [
  { name: 'Baixo risco cardiovascular', range: [0, 94], color: '#00c457' },
  { name: 'Risco aumentado', range: [94.1, 102], color: '#ffbb28' },
  { name: 'Risco muito aumentado', range: [102.1, 300], color: '#d30202' },
];
const FAIXAS_IAC_H = [
  { name: 'Normal', range: [8, 20] }, { name: 'Sobrepeso', range: [21, 25] }, { name: 'Obesidade', range: [26, 100] },
];
const FAIXAS_IAC_M = [
  { name: 'Normal', range: [21, 32] }, { name: 'Sobrepeso', range: [33, 38] }, { name: 'Obesidade', range: [39, 100] },
];
const FAIXAS_CONICIDADE = [
  { name: 'Bicôncavo — baixo risco', range: [0, 0.95] },
  { name: 'Cilíndrico — baixo risco', range: [0.96, 1.05] },
  { name: 'Bicônico — alto risco', range: [1.05, 3] },
];

// IRCQ depende de sexo e idade (espelha AntropometriaGeral.js).
const faixasIRCQ = (sexo, idade) => {
  const masc = ehMasculino(sexo);
  const i = Number(idade) || 0;
  const teto = masc ? 1.1 : 1;
  const faixa = (b, mo, a) => ([
    { name: 'Baixo risco', range: [0, b], color: '#00c457' },
    { name: 'Risco moderado', range: [b, mo], color: '#ffbb28' },
    { name: 'Risco alto', range: [mo, a], color: '#ff8042' },
    { name: 'Risco muito alto', range: [a, teto], color: '#fb1d1d' },
  ]);
  if (masc) {
    if (i <= 29) return faixa(0.83, 0.88, 0.94);
    if (i <= 39) return faixa(0.84, 0.91, 0.96);
    if (i <= 49) return faixa(0.88, 0.95, 1.0);
    if (i <= 59) return faixa(0.90, 0.96, 1.02);
    return faixa(0.91, 0.98, 1.03);
  }
  if (i <= 29) return faixa(0.71, 0.77, 0.82);
  if (i <= 39) return faixa(0.72, 0.78, 0.84);
  if (i <= 49) return faixa(0.73, 0.79, 0.87);
  if (i <= 59) return faixa(0.74, 0.81, 0.88);
  return faixa(0.76, 0.83, 0.90);
};

// --- Enunciados das escalas de idosos ---
const LABELS_KATZ = ['1. Banho', '2. Vestir', '3. Banheiro', '4. Transferência', '5. Continência', '6. Alimentação'];
const LABELS_LAWTON = [
  '1. Usar o telefone', '2. Ir a locais distantes (transporte)', '3. Fazer compras', '4. Preparar refeições',
  '5. Arrumar a casa', '6. Trabalhos manuais domésticos', '7. Lavar e passar roupa',
  '8. Tomar remédios (dose e horário)', '9. Cuidar das finanças',
];
const LABELS_BERG = [
  '1. Sentado para em pé', '2. Em pé sem apoio', '3. Sentado sem apoio', '4. Em pé para sentado',
  '5. Transferências', '6. Em pé (olhos fechados)', '7. Em pé (pés juntos)', '8. Alcançar à frente',
  '9. Pegar objeto do chão', '10. Olhar para trás', '11. Girar 360º', '12. Degrau / banquinho',
  '13. Pé à frente (tandem)', '14. Um pé só',
];
const LABELS_CAP_FUNCIONAL = [
  '1. Necessidades pessoais (vestir-se)', '2. Tomar banho', '3. Caminhar fora de casa (1–2 quarteirões)',
  '4. Atividades domésticas leves', '5. Subir/descer escadas', '6. Fazer compras',
  '7. Levantar e carregar 5 kg', '8. Caminhar 6–7 quarteirões', '9. Caminhar 12–14 quarteirões',
  '10. Levantar e carregar 13 kg', '11. Atividades domésticas pesadas', '12. Atividades vigorosas',
];

// Renderiza um array [{ id, valor }] como pergunta (via labels) | resposta.
const RespostasLabeladas = ({ respostas, labels, titulo = 'Respostas' }) => (
  <View style={{ marginTop: 8 }}>
    <Text style={styles.subtituloBloco}>{titulo}</Text>
    {respostas.map(({ id, valor }, i) => (
      <View key={i} style={styles.linhaParq} wrap={false}>
        <Text style={styles.perguntaParq}>{labels?.[id] ?? `Item ${Number(id) + 1}`}</Text>
        <Text style={styles.respostaParq}>{formatResposta(valor)}</Text>
      </View>
    ))}
  </View>
);

// R3: seção entra se tem pelo menos uma chave com valor preenchido.
const temConteudo = (obj) => {
  if (!obj || typeof obj !== 'object') return false;
  return Object.entries(obj).some(([, val]) => {
    if (val === undefined || val === null) return false;
    if (typeof val === 'string' && val.trim() === '') return false;
    if (Array.isArray(val) && val.length === 0) return false;
    if (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0) return false;
    return true;
  });
};

// Renderiza um conjunto de campos como tabela 2-col (label | valor).
const TabelaCampos = ({ campos }) => (
  <View style={styles.tabela}>
    {campos.map((c, i) => (
      <View key={i} style={[styles.linhaTabela, i % 2 === 1 ? styles.linhaTabelaAlt : null]} wrap={false}>
        <Text style={styles.celulaLabel}>{c.label}</Text>
        <Text style={styles.celulaValor}>{v(c.valor)}{c.unidade && c.valor !== undefined && c.valor !== null && c.valor !== '' ? ` ${c.unidade}` : ''}</Text>
      </View>
    ))}
  </View>
);

// ---------- Cabeçalhos / Rodapés (R5–R7) ----------

// Moldura preta repetida em todas as páginas.
const MolduraBorda = () => <View style={styles.moldura} fixed />;

const Header = ({ personal }) => (
  <View style={styles.header} fixed>
    <View style={styles.headerInfo}>
      <Text style={styles.personalNome}>{v(personal?.name)}</Text>
      <Text style={styles.personalContato}>{v(personal?.email)}</Text>
      {personal?.cref ? <Text style={styles.personalContato}>CREF: {personal.cref}</Text> : null}
    </View>
    {personal?.photo
      ? <Image src={personal.photo} style={styles.logoHeader} />
      : <View style={styles.logoHeader} />}
  </View>
);

// Título da seção como cabeçalho do retângulo de dados do aluno.
const BlocoTituloAluno = ({ titulo, aluno, evaluationData }) => {
  const a = evaluationData?.antropometria || {};
  return (
    <View style={styles.blocoAluno}>
      <Text style={styles.tituloTopico}>{titulo}</Text>
      <View style={styles.subHeader}>
        <View style={styles.subHeaderLinha}>
          <Text style={styles.subHeaderCampo}><Text style={styles.subHeaderLabel}>Nome: </Text>{v(aluno?.name)}</Text>
          <Text style={styles.subHeaderCampo}><Text style={styles.subHeaderLabel}>Idade: </Text>{v(aluno?.idade)}</Text>
          <Text style={styles.subHeaderCampo}><Text style={styles.subHeaderLabel}>Sexo: </Text>{formatSexo(aluno?.sexo)}</Text>
        </View>
        <View style={styles.subHeaderLinha}>
          <Text style={styles.subHeaderCampo}><Text style={styles.subHeaderLabel}>Peso: </Text>{v(a.peso)}{a.peso ? ' kg' : ''}</Text>
          <Text style={styles.subHeaderCampo}><Text style={styles.subHeaderLabel}>Estatura: </Text>{v(a.altura)}{a.altura ? ' cm' : ''}</Text>
          <Text style={styles.subHeaderCampo}><Text style={styles.subHeaderLabel}>Data da Avaliação: </Text>{formatDate(evaluationData?.evaluationDate)}</Text>
        </View>
      </View>
    </View>
  );
};

const Footer = ({ geradoEm }) => (
  <View style={styles.footer} fixed>
    <Text style={styles.footerEsq} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
    <Text style={styles.footerDir}>Gerado em {formatDateTime(geradoEm)}</Text>
  </View>
);

// ---------- Seções ----------

const SecaoAnamnese = ({ dados, perfil, observacoes }) => {
  const itens = normalizarRespostas(dados);
  if (itens.length === 0) return null;
  const labels = LABELS_ANAMNESE[perfil] || null;
  const temObs = typeof observacoes === 'string' && observacoes.trim() !== '';
  return (
    <View>
      <View style={styles.anamneseGrid}>
        {itens.map(({ id, valor }, i) => (
          <View key={i} style={styles.itemAnamnese} wrap={false}>
            <Text style={styles.perguntaAnamnese}>{labels?.[id] ?? `Pergunta ${Number(id) + 1}`}</Text>
            <Text style={styles.respostaAnamnese}>{formatResposta(valor)}</Text>
          </View>
        ))}
      </View>
      {temObs && (
        <View style={styles.anamneseObs}>
          <Text style={styles.anamneseObsTitulo}>Observações</Text>
          <Text style={styles.anamneseObsTexto}>{observacoes}</Text>
        </View>
      )}
    </View>
  );
};

const TERMO_PARQ = 'Estou ciente de que é recomendável conversar com um médico antes de aumentar meu nível atual de atividade física, por ter respondido "SIM" a uma ou mais perguntas do "Questionário de Prontidão para Atividade Física" (PAR-Q). Assumo plena responsabilidade por qualquer atividade física praticada sem o atendimento a essa recomendação.';

const SecaoParQ = ({ dados, dataAvaliacao }) => {
  const respostas = normalizarRespostas(dados?.respostas ?? dados);
  const responsabilidade = dados?.responsabilidade;
  if (respostas.length === 0) return null;
  return (
    <View>
      {respostas.map(({ id, valor }, i) => (
        <View key={i} style={styles.linhaParq} wrap={false}>
          <Text style={styles.perguntaParq}>{LABELS_PARQ[id] ?? `Pergunta ${Number(id) + 1}`}</Text>
          <Text style={styles.respostaParq}>{formatResposta(valor)}</Text>
        </View>
      ))}
      {responsabilidade && responsabilidade !== '-' ? (
        <View style={styles.parqTermo} wrap={false}>
          <Text style={styles.parqTermoTitulo}>Termo de Responsabilidade para Prática de Atividade Física</Text>
          <Text style={styles.parqTermoTexto}>{TERMO_PARQ}</Text>
          <Text style={styles.parqResponsabilidade}>
            Termo aceito pelo avaliado{formatResposta(responsabilidade) === 'Sim' ? '' : ` (${formatResposta(responsabilidade)})`} em {formatDate(dataAvaliacao)}.
          </Text>
        </View>
      ) : null}
    </View>
  );
};

const LABELS_CORONARIANO = [
  '1. Idade', '2. Hereditariedade', '3. Peso', '4. Fumo',
  '5. Pressão arterial', '6. Colesterol / gordura na dieta', '7. Exercícios', '8. Sexo',
];

// Faixas de pontuação do risco coronariano (mesma régua e cores do formulário).
const FAIXAS_CORONARIANO = [
  { name: 'Risco bem abaixo da média', range: [0, 11], color: '#00b341' },
  { name: 'Risco abaixo da média', range: [12, 17], color: '#62c902' },
  { name: 'Risco na média geral', range: [18, 24], color: '#e0c000' },
  { name: 'Risco moderado', range: [25, 31], color: '#ff8c00' },
  { name: 'Risco em nível perigoso', range: [32, 40], color: '#eb6a00' },
  { name: 'Risco avançado', range: [41, 60], color: '#ce0000' },
];

// Parágrafos 1, 3 e 4 do "Mais informações" do teste (Coronariano.js).
const CORONARIANO_INFO = [
  'O teste de risco coronariano é uma ferramenta de triagem que ajuda a identificar, de forma simples e rápida, a probabilidade de desenvolver doenças cardiovasculares com base em fatores como idade, histórico familiar, tabagismo, pressão arterial, colesterol, diabetes, peso corporal, alimentação, nível de atividade física e estresse.',
  'Seu principal objetivo não é diagnosticar doenças, mas alertar sobre hábitos e condições que podem aumentar o risco de problemas como hipertensão, infarto e outras doenças do coração. Quanto maior a pontuação, maior a necessidade de atenção e mudanças no estilo de vida.',
  'Utilize este resultado como um guia para adotar hábitos mais saudáveis, melhorar sua rotina e buscar acompanhamento profissional quando necessário. Prevenir é sempre o melhor caminho para proteger sua saúde cardiovascular.',
];

const SecaoCoronariana = ({ dados }) => {
  const respostas = normalizarRespostas(dados);
  const temRespostas = respostas.length > 0;
  // Recomputa a partir dos pesos salvos (o formulário não persiste o total nem o nível).
  const pontos = dados?.pontos ?? dados?.pontuacao
    ?? (temRespostas ? respostas.reduce((acc, r) => acc + (Number(r.peso) || 0), 0) : undefined);
  const faixaAtual = pontos !== undefined
    ? FAIXAS_CORONARIANO.find((f) => pontos >= f.range[0] && pontos <= f.range[1])
    : null;
  const classificacao = dados?.classificacao ?? dados?.resultado ?? faixaAtual?.name;
  const cor = faixaAtual?.color || COR_TEXTO;

  return (
    <View>
      {temRespostas && (
        <View style={styles.tabela}>
          {respostas.map(({ id, valor, peso }, i) => (
            <View key={i} style={[styles.linhaTabela, i % 2 === 1 ? styles.linhaTabelaAlt : null]} wrap={false}>
              <Text style={styles.coronarioPergunta}>{LABELS_CORONARIANO[id] ?? `Item ${Number(id) + 1}`}</Text>
              <Text style={styles.coronarioResposta}>{formatResposta(valor)}{peso !== undefined ? ` (${peso})` : ''}</Text>
            </View>
          ))}
        </View>
      )}

      {pontos !== undefined && (
        <View style={[styles.coronarioPainel, { borderColor: cor }]}>
          <View style={styles.coronarioPainelTopo}>
            <Text style={styles.coronarioPontos}>{pontos}<Text style={styles.coronarioPontosLabel}> pontos</Text></Text>
            {classificacao ? (
              <View style={[styles.coronarioBadge, { backgroundColor: cor }]}>
                <Text style={styles.coronarioBadgeTxt}>{classificacao}</Text>
              </View>
            ) : null}
          </View>
          <BarraFaixas valor={pontos} faixas={FAIXAS_CORONARIANO} />
        </View>
      )}

      <View style={styles.coronarioNota}>
        {CORONARIANO_INFO.map((p, i) => (
          <Text key={i} style={styles.coronarioNotaTxt}>{p}</Text>
        ))}
      </View>
    </View>
  );
};

// Barra segmentada por faixas (largura igual, como o front), com marcador na posição do valor.
const BarraFaixas = ({ valor, faixas }) => {
  if (!Array.isArray(faixas) || faixas.length === 0) return null;
  const n = Number(valor);
  const temValor = !Number.isNaN(n);
  const cores = CORES_FAIXAS_PADRAO;
  const min = faixas[0].range[0];
  const max = faixas[faixas.length - 1].range[1];

  // Índice da faixa atual, robusto a pequenas lacunas entre intervalos.
  let idxAtual = -1;
  if (temValor) {
    idxAtual = faixas.findIndex((f) => n >= f.range[0] && n <= f.range[1]);
    if (idxAtual < 0) {
      if (n < min) idxAtual = 0;
      else if (n > max) idxAtual = faixas.length - 1;
      else {
        idxAtual = faixas.findIndex((f) => n <= f.range[1]);
        if (idxAtual < 0) idxAtual = faixas.length - 1;
      }
    }
  }

  // Segmentos de largura igual; marcador posicionado dentro do segmento da faixa.
  let pos = 0;
  if (temValor && idxAtual >= 0) {
    const f = faixas[idxAtual];
    const denom = f.range[1] - f.range[0];
    const frac = denom ? Math.max(0, Math.min(1, (n - f.range[0]) / denom)) : 0.5;
    pos = (idxAtual + frac) / faixas.length;
  }

  const segW = 100 / faixas.length;
  const rotulo = (f, i) => {
    if (i === 0) return `${f.name} (até ${f.range[1]})`;
    if (i === faixas.length - 1) return `${f.name} (acima de ${f.range[0]})`;
    return `${f.name} (${f.range[0]} a ${f.range[1]})`;
  };

  return (
    <View style={styles.barraFaixasWrap}>
      <View style={styles.barraFaixasArea}>
        {temValor && <View style={[styles.barraFaixasTick, { left: `${pos * 100}%` }]} />}
        <View style={styles.barraFaixasTrack}>
          {faixas.map((f, i) => (
            <View key={i} style={{ width: `${segW}%`, height: 10, backgroundColor: f.color || cores[i % cores.length] }} />
          ))}
        </View>
      </View>
      <View style={styles.barraFaixasLegenda}>
        {faixas.map((f, i) => (
          <View key={i} style={styles.barraFaixasLegItem}>
            <View style={[styles.barraFaixasLegCor, { backgroundColor: f.color || cores[i % cores.length] }]} />
            <Text style={[styles.barraFaixasLegTxt, i === idxAtual ? styles.barraFaixasLegAtual : null]}>
              {rotulo(f, i)}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
};

// Barras horizontais comparando valores (ex.: medido vs. sugerido).
const BarraComparacao = ({ itens, max }) => {
  const valores = itens.map((i) => Number(i.valor)).filter((x) => !Number.isNaN(x));
  const escala = max || (valores.length ? Math.max(...valores) : 1) || 1;
  return (
    <View style={{ marginTop: 4 }}>
      {itens.map((it, i) => {
        const val = Number(it.valor);
        const w = !Number.isNaN(val) ? Math.max(0, Math.min(1, val / escala)) * 100 : 0;
        return (
          <View key={i} style={styles.barraLinha} wrap={false}>
            <Text style={styles.barraLabel}>{it.label}</Text>
            <View style={styles.barraTrack}>
              <View style={[styles.barraFill, { width: `${w}%`, backgroundColor: it.cor || COR_DESTAQUE }]} />
            </View>
            <Text style={styles.barraValor}>{!Number.isNaN(val) ? val.toFixed(1) : '—'}{it.unidade || ''}</Text>
          </View>
        );
      })}
    </View>
  );
};

// Gráfico de rosca (donut) desenhado em SVG, igual ao da tela de composição corporal.
const pontoPolar = (cx, cy, r, angGraus) => {
  const a = ((angGraus - 90) * Math.PI) / 180; // 0° no topo
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};

const setorRosca = (cx, cy, rOut, rIn, a0, a1) => {
  const laf = a1 - a0 > 180 ? 1 : 0;
  const [x0o, y0o] = pontoPolar(cx, cy, rOut, a0);
  const [x1o, y1o] = pontoPolar(cx, cy, rOut, a1);
  const [x1i, y1i] = pontoPolar(cx, cy, rIn, a1);
  const [x0i, y0i] = pontoPolar(cx, cy, rIn, a0);
  return `M ${x0o} ${y0o} A ${rOut} ${rOut} 0 ${laf} 1 ${x1o} ${y1o} L ${x1i} ${y1i} A ${rIn} ${rIn} 0 ${laf} 0 ${x0i} ${y0i} Z`;
};

// Texto escuro ou branco conforme a luminância da cor da fatia (legibilidade).
const corTextoFatia = (hex) => {
  const c = String(hex || '').replace('#', '');
  if (c.length < 6) return '#ffffff';
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#1f2937' : '#ffffff';
};

const Rosquinha = ({ itens, size = 150, espessura = 36, peso }) => {
  const total = itens.reduce((s, d) => s + (Number(d.valor) || 0), 0);
  if (total <= 0) return null;
  const cx = size / 2;
  const cy = size / 2;
  const rOut = size / 2;
  const rIn = rOut - espessura;
  const rMid = (rOut + rIn) / 2;
  const pesoNum = Number(peso);

  let ang = 0;
  const segs = itens.map((d) => {
    const val = Number(d.valor) || 0;
    const sweep = (val / total) * 360;
    const a0 = ang;
    const a1 = sweep >= 359.999 ? a0 + 359.999 : ang + sweep;
    ang += sweep;
    return { cor: d.cor, val, a0, a1, sweep };
  }).filter((s) => s.val > 0);

  return (
    <Svg width={size} height={size}>
      {segs.map((s, i) => (
        <Path key={i} d={setorRosca(cx, cy, rOut, rIn, s.a0, s.a1)} fill={s.cor} />
      ))}
      {segs.flatMap((s, i) => {
        if (s.sweep < 16) return []; // fatia muito fina para rótulo
        const [lx, ly] = pontoPolar(cx, cy, rMid, (s.a0 + s.a1) / 2);
        const cor = corTextoFatia(s.cor);
        const kg = !Number.isNaN(pesoNum) ? `${(pesoNum * s.val / 100).toFixed(1)}kg` : null;
        const out = [
          <Text key={`p${i}`} x={lx} y={kg ? ly : ly + 2.5} fill={cor} fontSize={7} textAnchor="middle">{`${s.val.toFixed(0)}%`}</Text>,
        ];
        if (kg) out.push(<Text key={`k${i}`} x={lx} y={ly + 8} fill={cor} fontSize={6} textAnchor="middle">{kg}</Text>);
        return out;
      })}
    </Svg>
  );
};

const PERIMETROS_LABELS = [
  ['Pescoço', 'pescoço'], ['Ombro', 'ombro'],
  ['Tórax Normal', 'toraxNormal'], ['Tórax Inspirado', 'toraxInspirado'],
  ['Abdômen', 'abdomen'], ['Cintura', 'cintura'], ['Quadril', 'quadril'],
  ['Coxa Prox. D', 'coxaDproximal'], ['Coxa Prox. E', 'coxaEproximal'],
  ['Coxa Medial D', 'coxaDmedial'], ['Coxa Medial E', 'coxaEmedial'],
  ['Perna D', 'pernaD'], ['Perna E', 'pernaE'],
  ['Braço Relax. D', 'bracoDrelaxado'], ['Braço Relax. E', 'bracoErelaxado'],
  ['Braço Contr. D', 'bracoDcontraido'], ['Braço Contr. E', 'bracoEcontraido'],
  ['Antebraço D', 'antebracoD'], ['Antebraço E', 'antebracoE'],
  ['Punho D', 'punhoD'], ['Punho E', 'punhoE'],
];

// Posição de cada medida na figura (percentual da largura/altura da imagem, vista frontal).
// Lado D = direito anatômico = lado esquerdo de quem olha. Pares D/E compartilham a cor.
const MARCACOES_PERIMETROS = [
  { chave: 'pescoço', nome: 'Pescoço', x: 50, y: 14.5, cor: '#e6194b' },
  { chave: 'ombro', nome: 'Ombro', x: 50, y: 20, cor: '#3cb44b' },
  { chave: 'toraxNormal', nome: 'Tórax Normal', x: 43, y: 26, cor: '#4363d8' },
  { chave: 'toraxInspirado', nome: 'Tórax Inspirado', x: 57, y: 26, cor: '#f58231' },
  { chave: 'cintura', nome: 'Cintura', x: 50, y: 36, cor: '#911eb4' },
  { chave: 'abdomen', nome: 'Abdômen', x: 50, y: 40.5, cor: '#46f0f0' },
  { chave: 'quadril', nome: 'Quadril', x: 50, y: 46, cor: '#f032e6' },
  { chave: 'coxaDproximal', nome: 'Coxa Proximal D', x: 39, y: 52, cor: '#ffe119' },
  { chave: 'coxaEproximal', nome: 'Coxa Proximal E', x: 61, y: 52, cor: '#ffe119' },
  { chave: 'coxaDmedial', nome: 'Coxa Medial D', x: 38, y: 60, cor: '#bcf60c' },
  { chave: 'coxaEmedial', nome: 'Coxa Medial E', x: 62, y: 60, cor: '#bcf60c' },
  { chave: 'pernaD', nome: 'Perna D', x: 36, y: 76, cor: '#fabebe' },
  { chave: 'pernaE', nome: 'Perna E', x: 64, y: 76, cor: '#fabebe' },
  { chave: 'bracoDrelaxado', nome: 'Braço Relaxado D', x: 21, y: 29, cor: '#008080' },
  { chave: 'bracoErelaxado', nome: 'Braço Relaxado E', x: 79, y: 29, cor: '#008080' },
  { chave: 'bracoDcontraido', nome: 'Braço Contraído D', x: 19, y: 33.5, cor: '#9a6324' },
  { chave: 'bracoEcontraido', nome: 'Braço Contraído E', x: 81, y: 33.5, cor: '#9a6324' },
  { chave: 'antebracoD', nome: 'Antebraço D', x: 15, y: 43, cor: '#800000' },
  { chave: 'antebracoE', nome: 'Antebraço E', x: 85, y: 43, cor: '#800000' },
  { chave: 'punhoD', nome: 'Punho D', x: 12, y: 51, cor: '#000075' },
  { chave: 'punhoE', nome: 'Punho E', x: 88, y: 51, cor: '#000075' },
];

const FiguraPerimetros = ({ dados, sexo, largura = 160 }) => {
  const altura = largura * (1300 / 600); // proporção das imagens (600 x 1300)
  const raio = 5;
  const marcadas = MARCACOES_PERIMETROS.filter((m) => {
    const n = Number(dados?.[m.chave]);
    return Number.isFinite(n) && n > 0;
  });
  return (
    <View style={{ width: largura, height: altura, position: 'relative' }}>
      <Image src={ehMasculino(sexo) ? figuraHomem : figuraMulher} style={{ width: largura, height: altura }} />
      {marcadas.map((m) => (
        <View
          key={m.chave}
          style={{
            position: 'absolute',
            left: (m.x / 100) * largura - raio,
            top: (m.y / 100) * altura - raio,
            width: raio * 2,
            height: raio * 2,
            borderRadius: raio,
            backgroundColor: m.cor,
            borderWidth: 1,
            borderColor: '#ffffff',
          }}
        />
      ))}
    </View>
  );
};

const SecaoAntropometriaPerimetros = ({ dados, sexo }) => {
  const sexoRef = dados?.sexo || sexo;
  const itens = MARCACOES_PERIMETROS.map((m) => ({ ...m, valor: dados?.[m.chave] }));
  const meio = Math.ceil(itens.length / 2);

  // Tabela completa das medidas (duas colunas), acima da figura.
  const campos = PERIMETROS_LABELS.map(([label, key]) => ({ label, valor: dados?.[key], unidade: 'cm' }));
  const meioTabela = Math.ceil(campos.length / 2);
  const colunaLegenda = (lista) => (
    <View style={{ flex: 1 }}>
      {lista.map((m) => {
        const n = Number(m.valor);
        const preenchido = Number.isFinite(n) && n > 0;
        return (
          <View key={m.chave} style={styles.legendaPerimetroItem}>
            <View style={[styles.legendaPerimetroCor, { backgroundColor: preenchido ? m.cor : '#d1d5db' }]} />
            <Text style={[styles.legendaPerimetroTxt, preenchido ? null : { color: '#9ca3af' }]}>
              {m.nome}: {preenchido ? `${n} cm` : '—'}
            </Text>
          </View>
        );
      })}
    </View>
  );
  return (
    <View>
      <View style={styles.duasColunas}>
        <View style={[styles.coluna, styles.colunaEsq]}>
          <TabelaCampos campos={campos.slice(0, meioTabela)} />
        </View>
        <View style={styles.coluna}>
          <TabelaCampos campos={campos.slice(meioTabela)} />
        </View>
      </View>

      <View style={[styles.perimetrosLayout, { marginTop: 4 }]} wrap={false}>
        <FiguraPerimetros dados={dados} sexo={sexoRef} largura={125} />
        <View style={styles.perimetrosLegenda}>
          <Text style={[styles.subtituloBloco, { marginBottom: 6 }]}>Pontos de medida</Text>
        <View style={{ flexDirection: 'row' }}>
          {colunaLegenda(itens.slice(0, meio))}
          {colunaLegenda(itens.slice(meio))}
        </View>
          <Text style={[styles.cardIndicadorNota, { marginTop: 8 }]}>
            Cada cor marca na figura o ponto da medida. Lado D = lado direito do avaliado (esquerda de quem olha). Medidas não preenchidas aparecem em cinza.
          </Text>
        </View>
      </View>
    </View>
  );
};

const CORES_FAIXAS_PADRAO = ['#66a3ff', '#00c457', '#ffbb28', '#ff8042', '#fb1d1d', '#d30202'];

// Cor da faixa em que um valor cai (usa a cor da faixa ou a paleta padrão, como a régua).
const corDaFaixa = (valor, faixas) => {
  const i = indiceDaFaixa(valor, faixas);
  if (i < 0) return null;
  return faixas[i].color || CORES_FAIXAS_PADRAO[i % CORES_FAIXAS_PADRAO.length];
};

// Cabeçalho padrão dos blocos de resultado: "Título — valor" (valor em destaque) e, à direita,
// a classificação como selo colorido pela faixa — o mesmo visual do risco coronariano.
const CabecalhoIndicador = ({ titulo, valor, unidade, classe, faixas, valorFaixa, cor }) => {
  const temValor = valor !== undefined && valor !== null && valor !== '' && !(typeof valor === 'number' && Number.isNaN(valor));
  const corSelo = cor || corDaFaixa(valorFaixa !== undefined ? valorFaixa : valor, faixas) || COR_TEXTO_SUAVE;
  return (
    <View style={styles.indicadorTopo}>
      <Text style={styles.indicadorTitulo}>
        {titulo}
        {temValor ? <Text style={styles.indicadorValor}>{`  ${valor}${unidade ? ` ${unidade}` : ''}`}</Text> : null}
      </Text>
      {classe ? (
        <View style={[styles.indicadorSelo, { backgroundColor: corSelo }]}>
          <Text style={[styles.indicadorSeloTxt, { color: '#ffffff' }]}>{classe}</Text>
        </View>
      ) : null}
    </View>
  );
};

// Caixinha de um indicador: cabeçalho padronizado + régua de faixas.
const CardIndicador = ({ titulo, valor, unidade, faixas }) => {
  const classe = classificar(valor, faixas);
  return (
    <View style={styles.cardIndicador} wrap={false}>
      <CabecalhoIndicador titulo={titulo} valor={v(valor)} unidade={unidade} classe={classe} faixas={faixas} />
      <BarraFaixas valor={valor} faixas={faixas} />
    </View>
  );
};

const SecaoAntropometriaIndices = ({ dados, sexo }) => {
  const masc = ehMasculino(sexo);
  return (
    <View>
      <CardIndicador titulo="Índice de Massa Corporal (IMC)" valor={dados?.imc} unidade="kg/m²" faixas={FAIXAS_IMC} />
      <CardIndicador titulo="Circunferência Abdominal" valor={dados?.abdomen} unidade="cm" faixas={FAIXAS_ABDOMEN} />
      <CardIndicador titulo="Relação Cintura-Quadril (IRCQ)" valor={dados?.IRCQ} faixas={faixasIRCQ(sexo, dados?.idade)} />
      <CardIndicador titulo="Índice Cintura-Altura (ICA)" valor={dados?.ICA} faixas={FAIXAS_ICA} />
      <CardIndicador titulo="Índice de Adiposidade Corporal (IAC)" valor={dados?.IAC} faixas={masc ? FAIXAS_IAC_H : FAIXAS_IAC_M} />
      <CardIndicador titulo="Índice de Conicidade" valor={dados?.conicidade} faixas={FAIXAS_CONICIDADE} />
    </View>
  );
};

const NIVEL_ATIVIDADE = {
  sedentario: 'Sedentário (× 1,2)',
  leve: 'Levemente ativo (× 1,375)',
  moderada: 'Moderadamente ativo (× 1,55)',
  alta: 'Altamente ativo (× 1,725)',
  extrema: 'Extremamente ativo (× 1,9)',
};

// Faixas de peso saudável a partir da altura (IMC 18,5–24,9), com bandas comparáveis para a escala.
const faixasPesoIdeal = (altura) => {
  const a = Number(altura) / 100;
  if (!a || Number.isNaN(a)) return null;
  const inf = a * a * 18.5;
  const sup = a * a * 24.9;
  const larg = sup - inf;
  return [
    { name: 'Abaixo do ideal', range: [Math.round(Math.max(0, inf - larg)), Math.round(inf)], color: '#ffbb28' },
    { name: 'Dentro do ideal', range: [Math.round(inf), Math.round(sup)], color: '#00c457' },
    { name: 'Acima do ideal', range: [Math.round(sup), Math.round(sup + larg)], color: '#fb1d1d' },
  ];
};

const SecaoValoresReferencia = ({ dados }) => {
  const peso = Number(dados?.peso);
  const altura = Number(dados?.altura);
  const tmb = Number(dados?.TMB);
  const get = Number(dados?.GET);
  const faixasPI = faixasPesoIdeal(altura);
  const inf = !Number.isNaN(altura) && altura > 0 ? (altura / 100) ** 2 * 18.5 : null;
  const sup = !Number.isNaN(altura) && altura > 0 ? (altura / 100) ** 2 * 24.9 : null;
  const temPeso = !Number.isNaN(peso) && peso > 0 && inf !== null;
  const classePeso = temPeso
    ? (peso < inf ? 'Abaixo do ideal' : peso <= sup ? 'Dentro do ideal' : 'Acima do ideal')
    : null;
  const nivel = NIVEL_ATIVIDADE[dados?.fatorAtividade] || (dados?.fatorAtividade ? String(dados.fatorAtividade) : null);

  // Quanto falta para entrar na faixa (ou a margem até os limites, quando já está dentro).
  const linhasPeso = [];
  if (inf !== null) {
    linhasPeso.push({ label: 'Limite inferior (IMC 18,5)', valor: inf.toFixed(1), unidade: 'kg' });
    linhasPeso.push({ label: 'Limite superior (IMC 24,9)', valor: sup.toFixed(1), unidade: 'kg' });
  }
  if (temPeso) {
    linhasPeso.push({ label: 'Peso atual', valor: peso.toFixed(1), unidade: 'kg' });
    if (peso > sup) linhasPeso.push({ label: 'Deve perder para o limite superior', valor: (peso - sup).toFixed(1), unidade: 'kg' });
    else if (peso < inf) linhasPeso.push({ label: 'Deve ganhar para o limite inferior', valor: (inf - peso).toFixed(1), unidade: 'kg' });
    else {
      linhasPeso.push({ label: 'Margem até o limite superior', valor: (sup - peso).toFixed(1), unidade: 'kg' });
      linhasPeso.push({ label: 'Margem até o limite inferior', valor: (peso - inf).toFixed(1), unidade: 'kg' });
    }
  }

  // Composição do GET (recomputada do que é salvo): ETA = 10% do GET; GEAF = GET − TMB − ETA.
  const eta = !Number.isNaN(get) ? get * 0.1 : null;
  const geaf = (!Number.isNaN(get) && !Number.isNaN(tmb) && eta !== null) ? get - tmb - eta : null;
  const itensGET = [
    { name: 'Taxa metabólica basal (TMB)', valor: tmb, cor: '#ffbb28' },
    { name: 'Gasto energético com atividade física (GEAF)', valor: geaf, cor: '#0088fe' },
    { name: 'Efeito térmico da alimentação (ETA)', valor: eta, cor: '#00c49f' },
  ];

  return (
    <View>
      <View style={styles.cardIndicador} wrap={false}>
        <CabecalhoIndicador
          titulo="Peso Padrão de Normalidade"
          valor={dados?.pesoIdeal} unidade="kg"
          classe={classePeso} faixas={faixasPI} valorFaixa={peso}
        />
        {faixasPI ? <BarraFaixas valor={peso} faixas={faixasPI} /> : null}
        {linhasPeso.length > 0 ? <View style={{ marginTop: 6 }}><TabelaCampos campos={linhasPeso} /></View> : null}
      </View>

      <View style={styles.cardIndicador} wrap={false}>
        <CabecalhoIndicador titulo="Taxa Metabólica Basal (TMB)" valor={!Number.isNaN(tmb) ? tmb.toFixed(2) : undefined} unidade="kcal" classe={nivel} />
        <Text style={styles.cardIndicadorNota}>Nível de atividade considerado no cálculo do gasto energético.</Text>
      </View>

      <View style={styles.cardIndicador} wrap={false}>
        <CabecalhoIndicador titulo="Gasto Energético Total (GET)" valor={!Number.isNaN(get) ? get.toFixed(2) : undefined} unidade="kcal" />
        {(eta !== null && geaf !== null) ? (
          <View>
            <Text style={styles.cardIndicadorNota}>Composição do gasto energético:</Text>
            <View style={styles.compResultadoRow}>
              <Rosquinha itens={itensGET} size={120} espessura={30} />
              <View style={styles.compLegenda}>
                {itensGET.map((it, i) => (
                  <View key={i} style={styles.compLegItem}>
                    <View style={[styles.compLegCor, { backgroundColor: it.cor }]} />
                    <Text style={styles.compLegTxt}>{it.name}: {Number(it.valor).toFixed(1)} kcal ({get > 0 ? ((Number(it.valor) / get) * 100).toFixed(0) : '—'}%)</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        ) : null}
      </View>
    </View>
  );
};

const PROTOCOLOS_LABEL = {
  'CUN-BAE': 'CUN-BAE (Clínica Universidad de Navarra)',
  'Weltman': 'Weltman et al. (1988)',
  'US Navy': 'Marinha Americana (U.S. Navy Method)',
  'Deurenberg': 'Deurenberg (1991)',
  'Falkner': 'Falkner — Homens e mulheres',
  'Sloan et all (1962)': 'Sloan et al. (1962) — Jovens (18–26 anos)',
  'Durnin & Rahaman (1967)': 'Durnin & Rahaman (1967) — H (18–34), M (18–29)',
  'Jackson e Pollock 7 Dobras': 'Jackson e Pollock — 7 Dobras',
  'Jackson e Pollock 3 Dobras': 'Jackson e Pollock — 3 Dobras',
  'Durmin - Wormesley (1974)': 'Durnin & Womersley (1974) — H e M (16–72 anos)',
  'Wilmore & Behnke (1969)': 'Wilmore & Behnke (1969)',
  'Guedes (1994) Brasileiros': 'Guedes (1994) — Brasileiros',
  'Petroski (1995) Brasileiros': 'Petroski (1995) — Brasileiros',
  'Slaughter et al (1988)': 'Slaughter et al. (1988) — Crianças e adolescentes',
};

const DOBRAS_LABELS = [
  ['Peitoral', 'peitoral'], ['Axilar média', 'axilar'], ['Supra-ilíaca', 'supraIliaca'],
  ['Subescapular', 'subescapular'], ['Abdominal', 'abdominal'], ['Tríceps', 'triceps'],
  ['Bíceps', 'biceps'], ['Coxa medial', 'coxa'], ['Panturrilha', 'panturrilha'],
];

// Replica avaliarPercentualGordura do formulário (perímetros/dobras).
const metaGordura = (gc, peso, masc) => {
  const faixas = masc ? FAIXAS_GORDURA_H : FAIXAS_GORDURA_M;
  const normal = faixas.find((f) => f.name === 'Normal');
  if (!normal || Number.isNaN(gc) || Number.isNaN(peso)) return null;
  const [inf, sup] = normal.range;
  if (gc < inf) {
    const diff = inf - gc;
    return {
      classificacao: 'Abaixo do normal',
      detalhe: `${diff.toFixed(2)}% abaixo do ideal`,
      gordura: `Deficiência de gordura: ${(diff / 100 * peso).toFixed(2)} kg`,
      limite: `Limite inferior do peso corporal: ${(peso + diff / 100 * peso).toFixed(2)} kg`,
    };
  }
  if (gc > sup) {
    const exc = gc - sup;
    const acima = faixas.find((f) => f.name === 'Acima do normal');
    return {
      classificacao: gc > (acima ? acima.range[1] : sup) ? 'Tendência à obesidade' : 'Acima do normal',
      detalhe: `${exc.toFixed(2)}% acima do ideal`,
      gordura: `Excesso de gordura: ${(exc / 100 * peso).toFixed(2)} kg`,
      limite: `Limite superior do peso corporal: ${(peso - exc / 100 * peso).toFixed(2)} kg`,
    };
  }
  return { classificacao: 'Adequado', detalhe: 'Dentro da faixa considerada ideal', gordura: '', limite: '' };
};

const SecaoComposicaoCorporal = ({ dados, antropometria, sexo }) => {
  const masc = ehMasculino(sexo || antropometria?.sexo);
  const metodo = dados?.metodoAvaliacao;
  const gc = Number(dados?.percentualGordura);
  const classeGordura = classificar(gc, masc ? FAIXAS_GORDURA_H : FAIXAS_GORDURA_M);

  const peso = Number(antropometria?.peso);
  const alturaM = Number(antropometria?.altura) / 100;
  const ffmi = (!Number.isNaN(peso) && !Number.isNaN(alturaM) && alturaM > 0 && !Number.isNaN(gc))
    ? (peso - (gc / 100) * peso) / (alturaM * alturaM)
    : null;
  const faixaFfmi = ffmi !== null
    ? (masc ? FAIXAS_FFMI_H : FAIXAS_FFMI_M).find((f) => ffmi >= f.range[0] && ffmi <= f.range[1])
    : null;

  const compItens = [
    { name: 'Gordura', valor: gc, cor: '#ffbb28' },
    { name: 'Massa Muscular', valor: Number(dados?.percentualMassaMuscular), cor: '#00c457' },
    { name: 'Massa Óssea', valor: Number(dados?.percentualMassaOssea), cor: '#0088fe' },
    { name: 'Peso Residual', valor: Number(dados?.percentualPesoResidual), cor: '#ff4242' },
  ];

  const metodoLabel = metodo === 'Direto'
    ? (dados?.metodoDiretoUtilizado ? `Direto — ${dados.metodoDiretoUtilizado}` : 'Direto')
    : metodo === 'Perimetros' ? 'Perímetros Corporais'
      : metodo === 'Dobras' ? 'Dobras Cutâneas'
        : metodo;
  const protocoloLabel = PROTOCOLOS_LABEL[dados?.protocoloAvaliacao] || dados?.protocoloAvaliacao;

  const meta = (metodo === 'Perimetros' || metodo === 'Dobras') ? metaGordura(gc, peso, masc) : null;

  const dobras = metodo === 'Dobras'
    ? DOBRAS_LABELS.map(([label, key]) => {
      const vals = [dados?.[`${key}1`], dados?.[`${key}2`], dados?.[`${key}3`]]
        .map((x) => parseFloat(x)).filter((x) => !Number.isNaN(x));
      const media = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
      return { label, media };
    }).filter((d) => d.media !== null)
    : [];

  return (
    <View>
      {/* Método e protocolo */}
      <TabelaCampos
        campos={[
          { label: 'Método', valor: metodoLabel },
          ...(metodo !== 'Direto' ? [{ label: 'Protocolo', valor: protocoloLabel }] : []),
        ]}
      />

      {/* Dados principais */}
      <View style={styles.tabela}>
        <View style={styles.linhaTabela} wrap={false}>
          <Text style={styles.celulaLabel}>Peso</Text>
          <Text style={styles.celulaValor}>{v(antropometria?.peso)}{antropometria?.peso ? ' kg' : ''}</Text>
          <Text style={styles.celulaLabel}>Estatura</Text>
          <Text style={styles.celulaValor}>{v(antropometria?.altura)}{antropometria?.altura ? ' cm' : ''}</Text>
        </View>
        <View style={[styles.linhaTabela, styles.linhaTabelaAlt]} wrap={false}>
          <Text style={styles.celulaLabel}>% Gordura</Text>
          <Text style={styles.celulaValor}>{v(dados?.percentualGordura)}{dados?.percentualGordura ? ' %' : ''}</Text>
          <Text style={styles.celulaLabel}>Massa Magra (MIG)</Text>
          <Text style={styles.celulaValor}>{!Number.isNaN(gc) ? `${(100 - gc).toFixed(1)} %` : '—'}</Text>
        </View>
        {metodo === 'Direto' && (
          <View style={styles.linhaTabela} wrap={false}>
            <Text style={styles.celulaLabel}>% Massa Muscular</Text>
            <Text style={styles.celulaValor}>{v(dados?.percentualMassaMuscular)}{dados?.percentualMassaMuscular ? ' %' : ''}</Text>
            <Text style={styles.celulaLabel}>% Massa Óssea</Text>
            <Text style={styles.celulaValor}>{v(dados?.percentualMassaOssea)}{dados?.percentualMassaOssea ? ' %' : ''}</Text>
          </View>
        )}
      </View>

      {/* Percentual de gordura — todos os métodos */}
      <View style={{ marginTop: 8 }}>
        <CabecalhoIndicador
          titulo="Percentual de gordura"
          valor={!Number.isNaN(gc) ? gc.toFixed(1) : undefined} unidade="%"
          classe={classeGordura} faixas={masc ? FAIXAS_GORDURA_H : FAIXAS_GORDURA_M} valorFaixa={gc}
        />
        <BarraFaixas valor={gc} faixas={masc ? FAIXAS_GORDURA_H : FAIXAS_GORDURA_M} />
      </View>

      {/* DIRETO: composição + FFMI + laudo */}
      {metodo === 'Direto' && (
        <View>
          <Text style={[styles.subtituloBloco, { marginBottom: 8 }]}>Informações e resultados</Text>
          <View style={styles.compResultadoRow}>
            <Rosquinha itens={compItens} size={140} espessura={34} peso={peso} />
            <View style={styles.compLegenda}>
              {compItens.map((it, i) => {
                const val = Number(it.valor);
                if (Number.isNaN(val)) return null;
                const kg = !Number.isNaN(peso) ? ` / ${(peso * val / 100).toFixed(2)} kg` : '';
                return (
                  <View key={i} style={styles.compLegItem}>
                    <View style={[styles.compLegCor, { backgroundColor: it.cor }]} />
                    <Text style={styles.compLegTxt}>{it.name}: {val.toFixed(1)}%{kg}</Text>
                  </View>
                );
              })}
            </View>
          </View>
          {(!Number.isNaN(peso) && !Number.isNaN(gc)) && (
            <View style={styles.tabela}>
              <View style={[styles.linhaTabela, styles.linhaTabelaHead]} wrap={false}>
                <Text style={styles.celNum3}>Peso total</Text>
                <Text style={styles.celNum3}>Peso gordo</Text>
                <Text style={styles.celNum3}>Massa isenta de gordura</Text>
              </View>
              <View style={styles.linhaTabela} wrap={false}>
                <Text style={styles.celNum3}>{peso.toFixed(2)} kg</Text>
                <Text style={styles.celNum3}>{(peso * gc / 100).toFixed(2)} kg</Text>
                <Text style={styles.celNum3}>{(peso - peso * gc / 100).toFixed(2)} kg</Text>
              </View>
            </View>
          )}
          {ffmi !== null && (
            <>
              <View style={{ marginTop: 8 }}>
                <CabecalhoIndicador
                  titulo="Índice de Massa Magra (FFMI)"
                  valor={ffmi.toFixed(2)} unidade="kg/m²"
                  classe={faixaFfmi?.name} faixas={masc ? FAIXAS_FFMI_H : FAIXAS_FFMI_M} valorFaixa={ffmi}
                />
                <BarraFaixas valor={ffmi} faixas={masc ? FAIXAS_FFMI_H : FAIXAS_FFMI_M} />
              </View>
            </>
          )}
          {faixaFfmi && (
            <View style={{ marginTop: 8 }}>
              <Text style={styles.anamneseObsTexto}><Text style={{ fontWeight: 'bold' }}>Laudo: </Text>{faixaFfmi.laudo}</Text>
              <Text style={[styles.anamneseObsTexto, { marginTop: 4 }]}><Text style={{ fontWeight: 'bold' }}>Sugestão de treino: </Text>{faixaFfmi.treino}</Text>
            </View>
          )}
        </View>
      )}

      {/* PERÍMETROS / DOBRAS: meta de gordura */}
      {meta && (
        <View style={{ marginTop: 6 }}>
          <Text style={[styles.subtituloBloco, { marginBottom: 4 }]}>Meta de gordura corporal</Text>
          <TabelaCampos
            campos={[
              { label: 'Classificação', valor: meta.classificacao },
              { label: 'Diferença', valor: meta.detalhe },
              ...(meta.gordura ? [{ label: 'Gordura', valor: meta.gordura }] : []),
              ...(meta.limite ? [{ label: 'Peso de referência', valor: meta.limite }] : []),
            ]}
          />
        </View>
      )}

      {/* DOBRAS: médias das dobras */}
      {dobras.length > 0 && (
        <View style={{ marginTop: 6 }}>
          <Text style={[styles.subtituloBloco, { marginBottom: 4 }]}>Dobras cutâneas (médias)</Text>
          <View style={styles.tabela}>
            {dobras.map((d, i) => (
              <View key={i} style={[styles.linhaTabela, i % 2 === 1 ? styles.linhaTabelaAlt : null]} wrap={false}>
                <Text style={styles.celulaLabel}>{d.label}</Text>
                <Text style={styles.celulaValor}>{d.media.toFixed(1)} mm</Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
};

// --- Capacidade cardiorrespiratória (VO2) — espelha testes, tabelas e cores do formulário (Vo2.js) ---

const TESTES_VO2 = {
  bruce: { nome: 'Teste máximo de Bruce', equipamento: 'Esteira' },
  balke: { nome: 'Teste submáximo de Balke e Ware', equipamento: 'Esteira' },
  ellestad: { nome: 'Teste máximo de Ellestad', equipamento: 'Esteira' },
  coconi: { nome: 'Teste de Coconi', equipamento: 'Esteira' },
  ventilatorio: { nome: 'Teste incremental — limiares ventilatórios', equipamento: 'Esteira' },
  astrand: { nome: 'Teste máximo de Astrand e Rodahl', equipamento: 'Bicicleta' },
  astrand8min: { nome: 'Teste submáximo de Astrand 8 minutos', equipamento: 'Bicicleta' },
  astrandBic: { nome: 'Teste submáximo de Astrand-Rhyming (carga única)', equipamento: 'Bicicleta' },
  bruceBic: { nome: 'Teste máximo de Bruce', equipamento: 'Bicicleta' },
  fox: { nome: 'Teste submáximo de Fox', equipamento: 'Bicicleta' },
  cooper: { nome: 'Teste de Cooper 12 minutos', equipamento: 'Pista' },
  rockport: { nome: 'Teste de 1 milha (Rockport — 1.600 m)', equipamento: 'Pista' },
  ukk: { nome: 'Teste de UKK (2.000 m)', equipamento: 'Pista' },
  banco: { nome: 'Teste submáximo em banco (Katch e McArdle, 1972)', equipamento: 'Banco' },
  margaria: { nome: 'Teste de potência anaeróbia de Margaria e Kalamen', equipamento: 'Escada' },
};

const NOMES_FAIXAS_VO2 = ['Muito baixo', 'Baixo', 'Insatisfatório', 'Regular', 'Bom', 'Muito bom', 'Excelente'];
const CORES_FAIXAS_VO2 = {
  'Muito baixo': '#d70909', 'Baixo': '#ff5e00', 'Insatisfatório': '#FFD700',
  'Regular': '#b3e714', 'Bom': '#32CD32', 'Muito bom': '#00BFFF', 'Excelente': '#4B0082',
};
const NOMES_FAIXAS_COOPER = ['Muito fraco', 'Fraco', 'Aceitável', 'Muito bom', 'Excelente'];
const CORES_FAIXAS_COOPER = {
  'Muito fraco': '#d70909', 'Fraco': '#ff5e00', 'Aceitável': '#FFD700', 'Muito bom': '#00BFFF', 'Excelente': '#32CD32',
};

// Limites entre categorias por faixa etária (mesmos números das tabelas do formulário).
const CORTES_VO2_H = {
  '25-': [30, 36, 41, 46, 51, 60], '26-35': [30, 34, 39, 42, 48, 56], '36-45': [26, 30, 34, 38, 42, 51],
  '46-55': [25, 28, 31, 35, 38, 45], '56-65': [22, 25, 29, 31, 35, 41], '65+': [20, 21, 25, 28, 31, 37],
};
const CORTES_VO2_M = {
  '25-': [28, 32, 37, 41, 46, 56], '26-35': [26, 30, 34, 38, 44, 52], '36-45': [22, 26, 30, 33, 37, 45],
  '46-55': [20, 24, 27, 30, 33, 40], '56-65': [18, 21, 24, 27, 31, 37], '65+': [17, 18, 22, 24, 27, 32],
};
const CORTES_COOPER_H = {
  '13-19': [2090, 2200, 2510, 2770], '20-29': [1960, 2110, 2400, 2640], '30-39': [1900, 2090, 2400, 2510],
  '40-49': [1830, 1990, 2240, 2460], '50-59': [1660, 1870, 2090, 2320], '60+': [1400, 1640, 1930, 2120],
};
const CORTES_COOPER_M = {
  '13-19': [1600, 1900, 2080, 2300], '20-29': [1550, 1790, 1970, 2160], '30-39': [1510, 1690, 1960, 2080],
  '40-49': [1420, 1580, 1790, 2000], '50-59': [1350, 1500, 1690, 1900], '60+': [1260, 1390, 1590, 1750],
};

const montarFaixasVo2 = (cortes, nomes, cores, teto) => {
  if (!Array.isArray(cortes)) return [];
  const limites = [0, ...cortes, teto];
  return nomes.map((name, i) => ({ name, range: [limites[i], limites[i + 1]], color: cores[name] || '#999' }));
};

const faixasVo2Geral = (sexo, idade) => {
  const i = Number(idade);
  if (!Number.isFinite(i) || i <= 0) return [];
  const chave = i <= 25 ? '25-' : i <= 35 ? '26-35' : i <= 45 ? '36-45' : i <= 55 ? '46-55' : i <= 65 ? '56-65' : '65+';
  return montarFaixasVo2((ehMasculino(sexo) ? CORTES_VO2_H : CORTES_VO2_M)[chave], NOMES_FAIXAS_VO2, CORES_FAIXAS_VO2, 150);
};

const faixasCooper = (sexo, idade) => {
  const i = Number(idade);
  if (!Number.isFinite(i) || i <= 0) return [];
  const chave = i <= 19 ? '13-19' : i <= 29 ? '20-29' : i <= 39 ? '30-39' : i <= 49 ? '40-49' : i <= 59 ? '50-59' : '60+';
  return montarFaixasVo2((ehMasculino(sexo) ? CORTES_COOPER_H : CORTES_COOPER_M)[chave], NOMES_FAIXAS_COOPER, CORES_FAIXAS_COOPER, 8000);
};

// O formulário zera em 0 os campos não usados pelo teste; só valor positivo conta como preenchido.
const numPos = (x) => {
  const n = Number(x);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const textoPreenchido = (x) => {
  if (x === undefined || x === null) return null;
  const s = String(x).trim();
  return s === '' || s === '0' ? null : s;
};

// Gate da página: só entra no relatório se algum teste tem resultado de fato registrado.
const temResultadoVo2 = (d) => Boolean(d && (numPos(d.vo2) || numPos(d.potencia)));

// Estatísticas em pares (duas caixinhas por linha); item sozinho ocupa meia largura.
const GradeStatsVo2 = ({ itens }) => {
  const pares = [];
  for (let i = 0; i < itens.length; i += 2) pares.push(itens.slice(i, i + 2));
  return (
    <View>
      {pares.map((par, i) => (
        <View key={i} style={styles.vo2StatsRow}>
          {par.map((it, j) => (
            <View key={j} style={[styles.vo2Stat, j === 0 && par.length > 1 ? styles.vo2StatEsq : null]}>
              <Text style={styles.vo2StatLabel}>{it.label}</Text>
              <Text style={styles.vo2StatValor}>
                {it.valor}
                {it.unidade ? <Text style={styles.vo2StatUnidade}> {it.unidade}</Text> : null}
              </Text>
            </View>
          ))}
          {par.length === 1 ? <View style={styles.vo2StatFantasma} /> : null}
        </View>
      ))}
    </View>
  );
};

// Card de um teste: título + badge de classificação, stats em pares, faixinha e blocos extras.
const CardTesteVo2 = ({ titulo, equipamento, classificacao, stats, tituloBarra, valorBarra, faixas, children }) => (
  <View style={styles.cardIndicador} wrap={false}>
    <View style={styles.vo2CardTopo}>
      <Text style={styles.vo2CardTitulo}>{titulo}</Text>
      {classificacao ? (
        <View style={[styles.vo2Badge, { backgroundColor: classificacao.color }]}>
          <Text style={[styles.vo2BadgeTxt, { color: '#ffffff' }]}>{classificacao.name}</Text>
        </View>
      ) : null}
    </View>
    {equipamento ? <Text style={styles.vo2CardSub}>Equipamento: {equipamento}</Text> : null}
    {stats && stats.length > 0 ? <GradeStatsVo2 itens={stats} /> : null}
    {Array.isArray(faixas) && faixas.length > 0 ? (
      <>
        <Text style={styles.barraTituloIndice}>{tituloBarra}</Text>
        <BarraFaixas valor={valorBarra} faixas={faixas} />
      </>
    ) : null}
    {children}
  </View>
);

const SecaoVo2 = ({ dados, antropometria, sexo, idade }) => {
  const d = dados || {};
  const sexoRef = antropometria?.sexo ?? sexo;
  const idadeRef = antropometria?.idade ?? idade;
  const peso = numPos(antropometria?.peso);

  const metodo = d.metodo || d.metodoAtual;
  const teste = TESTES_VO2[metodo];
  const vo2 = numPos(d.vo2);
  const potencia = numPos(d.potencia);

  const cards = [];

  // Teste de VO2 máx com resultado registrado.
  if (vo2 !== null && metodo !== 'margaria') {
    const ehCooper = metodo === 'cooper';
    const distancia = numPos(d.distancia);
    const faixas = ehCooper ? faixasCooper(sexoRef, idadeRef) : faixasVo2Geral(sexoRef, idadeRef);
    const valorBarra = ehCooper ? distancia : vo2;
    const classificacao = valorBarra !== null
      ? faixas.find((f) => valorBarra >= f.range[0] && valorBarra <= f.range[1])
      : null;

    const stats = [
      { label: 'VO2 máx relativo', valor: vo2, unidade: 'ml/kg/min' },
      ...(peso !== null ? [{ label: 'VO2 máx absoluto', valor: ((vo2 * peso) / 1000).toFixed(1), unidade: 'L/min' }] : []),
      { label: 'Capacidade metabólica', valor: (vo2 / 3.5).toFixed(1), unidade: 'METs' },
      ...(numPos(d.fcmax) !== null ? [{ label: 'FC máxima', valor: numPos(d.fcmax), unidade: 'bpm' }] : []),
      ...(numPos(d.fcRepouso) !== null ? [{ label: 'FC de repouso', valor: numPos(d.fcRepouso), unidade: 'bpm' }] : []),
      ...(distancia !== null ? [{ label: 'Distância percorrida', valor: distancia, unidade: 'm' }] : []),
      ...(textoPreenchido(d.tempo) !== null ? [{ label: 'Tempo do teste', valor: textoPreenchido(d.tempo), unidade: 'min' }] : []),
      ...(numPos(d.velocidade) !== null ? [{ label: 'Velocidade final', valor: numPos(d.velocidade), unidade: 'km/h' }] : []),
      ...(numPos(d.watts) !== null ? [{ label: 'Carga do teste', valor: numPos(d.watts), unidade: 'W' }] : []),
    ];

    // Limiares só existem nos testes que os medem — o formulário calcula a partir da tabela de FC
    // de qualquer teste, então sem esse recorte um Bruce sairia com limiares que ele não tem.
    const temLimiares = metodo === 'coconi' || metodo === 'astrand';
    const temVelocidadesLimiares = metodo === 'ventilatorio';

    // Limiares aeróbio/anaeróbio (Coconi/Astrand): linha só entra se o dado existir.
    const l1 = !temLimiares ? [] : [
      ['Velocidade', textoPreenchido(d.velocidadePrimeiroLimiar), ''],
      ['FC', textoPreenchido(d.fcPrimeiroLimiar), ' bpm'],
      ['% da FC de pico', textoPreenchido(d.fcPicoPrimeiroLimiarmax), '%'],
    ].filter(([, val]) => val !== null);
    const l2 = !temLimiares ? [] : [
      ['Velocidade', textoPreenchido(d.velocidadeSegundoLimiar), ''],
      ['FC', textoPreenchido(d.fcSegundoLimiar), ' bpm'],
      ['% da FC de pico', textoPreenchido(d.fcPicoSegundoLimiarmax), '%'],
    ].filter(([, val]) => val !== null);

    // Velocidades dos limiares ventilatórios (teste incremental).
    const vlv1 = temVelocidadesLimiares ? numPos(d.vlv1) : null;
    const vlv2 = temVelocidadesLimiares ? numPos(d.vlv2) : null;

    const preditivo = numPos(d.vo2Preditivo);
    const fcEstim = numPos(d.fcmaxEstimada);

    cards.push(
      <CardTesteVo2
        key="vo2"
        titulo={teste?.nome ?? 'Teste de VO2 máx'}
        equipamento={textoPreenchido(d.tipoTeste) ?? teste?.equipamento}
        classificacao={classificacao}
        stats={stats}
        tituloBarra={ehCooper ? 'Classificação pela distância percorrida (m)' : 'Classificação do VO2 máx (ml/kg/min)'}
        valorBarra={valorBarra}
        faixas={faixas}
      >
        {(l1.length > 0 || l2.length > 0) && (
          <View style={styles.vo2SubBloco}>
            <Text style={styles.subtituloBloco}>Limiares ventilatórios</Text>
            <View style={styles.vo2LimiaresRow}>
              {l1.length > 0 && (
                <View style={[styles.vo2LimiarCol, l2.length > 0 ? styles.vo2StatEsq : null]}>
                  <Text style={styles.vo2LimiarTitulo}>Limiar aeróbio (L1)</Text>
                  {l1.map(([rotulo, val, un], i) => (
                    <Text key={i} style={styles.vo2LimiarLinha}>
                      {rotulo}: <Text style={styles.vo2LimiarValor}>{val}{un}</Text>
                    </Text>
                  ))}
                </View>
              )}
              {l2.length > 0 && (
                <View style={styles.vo2LimiarCol}>
                  <Text style={styles.vo2LimiarTitulo}>Limiar anaeróbio (L2)</Text>
                  {l2.map(([rotulo, val, un], i) => (
                    <Text key={i} style={styles.vo2LimiarLinha}>
                      {rotulo}: <Text style={styles.vo2LimiarValor}>{val}{un}</Text>
                    </Text>
                  ))}
                </View>
              )}
            </View>
          </View>
        )}
        {(vlv1 !== null || vlv2 !== null) && (
          <View style={styles.vo2SubBloco}>
            <Text style={styles.subtituloBloco}>Velocidades dos limiares ventilatórios</Text>
            <GradeStatsVo2
              itens={[
                ...(vlv1 !== null ? [{ label: 'Limiar ventilatório 1 (VLV1)', valor: vlv1, unidade: 'km/h' }] : []),
                ...(vlv2 !== null ? [{ label: 'Limiar ventilatório 2 (VLV2)', valor: vlv2, unidade: 'km/h' }] : []),
              ]}
            />
          </View>
        )}
        {(preditivo !== null || fcEstim !== null) && (
          <Text style={styles.cardIndicadorNota}>
            Valores de referência (estimados por idade e gordura corporal):
            {preditivo !== null ? ` VO2 estimado ${preditivo} ml/kg/min` : ''}
            {preditivo !== null && fcEstim !== null ? ' · ' : ''}
            {fcEstim !== null ? ` FC máxima estimada ${fcEstim} bpm` : ''}
          </Text>
        )}
      </CardTesteVo2>
    );
  }

  // Potência anaeróbia (Margaria e Kalamen) — registrada em campo próprio, pode coexistir com outro teste.
  if (potencia !== null) {
    cards.push(
      <CardTesteVo2
        key="margaria"
        titulo={TESTES_VO2.margaria.nome}
        equipamento={TESTES_VO2.margaria.equipamento}
        stats={[{ label: 'Potência anaeróbia', valor: potencia, unidade: 'ml/kg/min' }]}
      />
    );
  }

  return cards.length > 0 ? <View>{cards}</View> : null;
};

// --- Prescrição de treino aeróbico — espelha zonas, fórmulas e periodização do formulário (PrescricaoTreinoAerobico.js) ---

const ZONAS_PTA = {
  Z1: 'Z1 – Regenerativa', Z2: 'Z2 – Lipolítica', Z3: 'Z3 – Condicionamento',
  Z4: 'Z4 – Limiar anaeróbio', Z5: 'Z5 – Esforço máximo',
};
const INTENSIDADES_PTA_FCR = { Z1: [50, 60], Z2: [60, 70], Z3: [70, 80], Z4: [80, 90], Z5: [90, 100] };
const INTENSIDADES_PTA_FCMAX = { Z1: [28, 42], Z2: [43, 56], Z3: [57, 70], Z4: [71, 83], Z5: [84, 100] };

// Percentuais de periodização por sessão (52 sessões por nível, mesmos números do formulário).
const PERC_SESSOES_PTA = {
  'Iniciante': [
    100, 104, 108, 96, 112, 120, 128, 112, 96, 104, 112, 120,
    112, 128, 132, 112, 96, 104, 112, 128, 132, 140, 148, 136, 128, 120, 112, 104,
    120, 128, 132, 112, 96, 104, 112, 128, 132, 140, 148, 136, 128, 120, 112,
    104, 120, 128, 132, 112, 96, 104, 112, 120,
  ],
  'Intermediário': [
    100, 108, 124, 100, 124, 128, 128, 96, 80, 64, 144, 128,
    128, 144, 144, 96, 80, 64, 144, 128, 160, 168, 136, 128, 112, 96, 128,
    144, 144, 96, 80, 64, 144, 128, 160, 168, 136, 128, 112, 96,
    128, 144, 144, 96, 80, 128, 136, 152, 168, 136, 128, 112,
  ],
  'Avançado': [
    100, 112, 124, 100, 136, 148, 152, 128, 104, 80, 176, 152,
    176, 176, 128, 104, 80, 176, 152, 200, 212, 176, 152, 128, 104, 128, 176,
    176, 128, 104, 80, 176, 152, 200, 212, 176, 152, 128, 104, 128,
    176, 176, 132, 104, 176, 152, 188, 208, 176, 152, 128, 104,
  ],
};

// Escala de Borg: [borg, adaptada, descrição] por linha; linha ativa espelha o destaque do formulário.
const LINHAS_BORG = [
  [6, 1, ''], [7, 1, 'Muito, muito leve'], [8, 1, ''], [9, 2, 'Muito leve'], [10, 3, ''],
  [11, 3, 'Moderadamente leve'], [12, 4, ''], [13, 5, 'Um pouco pesado'], [14, 6, ''], [15, 6, 'Pesado'],
  [16, 7, ''], [17, 8, 'Muito pesado'], [18, 9, ''], [19, 10, 'Muito, muito pesado'], [20, 10, ''],
];

const borgAlvo = (intensidade) => {
  const i = Number(intensidade);
  if (!Number.isFinite(i) || i <= 0) return null;
  const linha = Math.min(15, Math.max(1, Math.round((i / 100) * 14) + 1));
  const [borg] = LINHAS_BORG[linha - 1];
  const descricao = LINHAS_BORG[linha - 1][2] || LINHAS_BORG[linha]?.[2] || LINHAS_BORG[linha - 2]?.[2] || '';
  return { borg, descricao };
};

// Gate da página: Manual precisa de treinos gerados; Automático entra com os parâmetros salvos.
const temConteudoPrescAero = (d) => {
  if (!d) return false;
  if (d.tipoPrescricao === 'Manual') return Array.isArray(d.treinos) && d.treinos.length > 0;
  return d.tipoPrescricao === 'Automatico';
};

// Iconezinhos dos bloquinhos de resultado (a fonte padrão do PDF não renderiza emoji).
const IconeCoracao = () => (
  <Svg width={10} height={10} viewBox="0 0 12 12">
    <Path d="M6 10.8 C2.2 8 0.6 5.8 0.6 3.9 C0.6 2.2 1.9 1 3.4 1 C4.4 1 5.4 1.6 6 2.5 C6.6 1.6 7.6 1 8.6 1 C10.1 1 11.4 2.2 11.4 3.9 C11.4 5.8 9.8 8 6 10.8 Z" fill="#e73400" />
  </Svg>
);
const IconeChama = () => (
  <Svg width={10} height={10} viewBox="0 0 12 12">
    <Path d="M6 0.4 C6.3 2.4 4 3.6 3.2 5.4 C2.4 7.2 3.2 9.6 5 10.6 C4.6 9.6 4.7 8.6 5.4 7.9 C6.1 7.2 6.6 6.6 6.8 5.8 C7.8 6.8 8.6 8 8.4 9.4 C8.3 9.9 8.1 10.3 7.8 10.7 C9.5 9.9 10.6 8.2 10.6 6.4 C10.6 3.4 7 2.6 6 0.4 Z" fill="#f94c1a" />
  </Svg>
);
const IconeRelogio = () => (
  <Svg width={10} height={10} viewBox="0 0 12 12">
    <Path d="M6 0.6 A5.4 5.4 0 1 0 6 11.4 A5.4 5.4 0 1 0 6 0.6 Z M6 1.8 A4.2 4.2 0 1 1 6 10.2 A4.2 4.2 0 1 1 6 1.8 Z M5.5 3 L5.5 6.3 L8.2 7.9 L8.8 6.9 L6.5 5.6 L6.5 3 Z" fill="#27ae60" />
  </Svg>
);

const PrescAeroManual = ({ d }) => (
  <View>
    {textoPreenchido(d.nomeDoTreino) ? (
      <Text style={[styles.subtituloBloco, { marginBottom: 8 }]}>{d.nomeDoTreino}</Text>
    ) : null}
    <View style={styles.ptaGrade}>
      {d.treinos.map((treino, i) => (
        <View key={i} style={styles.ptaPostit} wrap={false}>
          <Text style={styles.ptaPostitTitulo}>{v(treino?.titulo)}</Text>
          {treino?.dayoff ? (
            <Text style={styles.ptaPostitDayoff}>Dia de descanso (dayoff)</Text>
          ) : (
            <Text style={styles.ptaPostitDesc}>{v(treino?.descricao)}</Text>
          )}
        </View>
      ))}
    </View>
  </View>
);

const PrescAeroAutomatico = ({ d, antropometria, vo2Dados }) => {
  const peso = numPos(antropometria?.peso);
  const idade = numPos(antropometria?.idade);
  const vo2max = numPos(vo2Dados?.vo2);
  const intensidade = numPos(d.intensidade);
  const fcRepouso = numPos(d.FCRepouso);
  const fcReserva = numPos(d.FCReserva);
  const demandaProposta = numPos(d.demandaProposta);

  const metodo = d.metodo === 'FCR' || d.metodo === 'FCMAX' ? d.metodo : null;
  const zona = metodo === 'FCR' ? INTENSIDADES_PTA_FCR[d.objetivo] : INTENSIDADES_PTA_FCMAX[d.objetivo];

  // Mesmas fórmulas do formulário: Tanaka, Karvonen, VO2 de treino, METs e gasto calórico.
  const fcMax = idade !== null ? 208 - 0.7 * idade : null;
  const fcAlvoMin = (fcRepouso !== null && fcReserva !== null && zona) ? Math.round(fcRepouso + fcReserva * (zona[0] / 100)) : null;
  const fcAlvoMax = (fcRepouso !== null && fcReserva !== null && zona) ? Math.round(fcRepouso + fcReserva * (zona[1] / 100)) : null;
  const vo2Alvo = (vo2max !== null && intensidade !== null) ? (intensidade / 100) * (vo2max - 3.5) + 3.5 : null;
  const mets = vo2Alvo !== null ? vo2Alvo / 3.5 : null;
  const kcalMin = (mets !== null && peso !== null) ? (mets * 3.5 * peso) / 200 : null;
  const demandaMinima = peso !== null ? (300 * peso) / 70 : null;
  const duracaoMinima = (demandaMinima !== null && kcalMin !== null && kcalMin > 0) ? Math.round(demandaMinima / kcalMin) : null;
  const duracaoProposta = (demandaProposta !== null && kcalMin !== null && kcalMin > 0) ? Math.round(demandaProposta / kcalMin) : null;

  const pse = borgAlvo(d.intensidade);

  // Plano de sessões: mesma lógica da tela (percentual de periodização sobre a duração proposta).
  const nivel = d.nivel === 'Intermediário' || d.nivel === 'Avançado' ? d.nivel : 'Iniciante';
  const percs = PERC_SESSOES_PTA[nivel];
  const selecionados = Array.isArray(d.selecionados) && d.selecionados.length > 0 ? d.selecionados : null;
  const numSessoes = numPos(d.numSessoes) !== null ? Math.floor(numPos(d.numSessoes)) : 0;
  const sessoes = Array.from({ length: numSessoes }, (_, i) => ({
    sessao: i + 1,
    exercicio: selecionados ? selecionados[i % selecionados.length] : '—',
    tempo: duracaoProposta !== null ? Math.round(duracaoProposta * (percs[i % percs.length] / 100)) : null,
  }));

  return (
    <View>
      {/* Parâmetros da prescrição */}
      <TabelaCampos
        campos={[
          {
            label: 'Cálculo da zona alvo',
            valor: metodo === 'FCR' ? 'Frequência Cardíaca de Reserva'
              : metodo === 'FCMAX' ? 'Frequência Cardíaca Máxima' : undefined,
          },
          { label: 'Objetivo do trabalho aeróbico', valor: ZONAS_PTA[d.objetivo] },
          { label: 'Zona de intensidade', valor: zona ? `${zona[0]}% – ${zona[1]}%` : undefined },
          { label: 'Intensidade proposta', valor: intensidade, unidade: '%' },
          ...(fcRepouso !== null ? [{ label: 'FC de repouso', valor: fcRepouso, unidade: 'bpm' }] : []),
          ...(fcReserva !== null ? [{ label: 'FC de reserva', valor: fcReserva, unidade: 'bpm' }] : []),
          ...(demandaMinima !== null ? [{ label: 'Demanda mínima sugerida', valor: demandaMinima.toFixed(0), unidade: 'kcal' }] : []),
          ...(demandaProposta !== null ? [{ label: 'Demanda energética proposta', valor: demandaProposta, unidade: 'kcal' }] : []),
        ]}
      />

      {/* Bloquinhos de resultado, como na tela */}
      <View style={styles.ptaCardsRow}>
        <View style={[styles.ptaCard, styles.ptaCardEsq]} wrap={false}>
          <View style={styles.ptaCardHeader}>
            <IconeCoracao />
            <Text style={styles.ptaCardTitulo}>Zona alvo de treino</Text>
          </View>
          {zona ? (
            <View style={styles.ptaLinhaDado}>
              <Text style={styles.ptaLinhaLabel}>Zona alvo</Text>
              <Text style={styles.ptaLinhaValor}>{ZONAS_PTA[d.objetivo]} ({zona[0]}–{zona[1]}%)</Text>
            </View>
          ) : null}
          {fcMax !== null ? (
            <View style={styles.ptaLinhaDado}>
              <Text style={styles.ptaLinhaLabel}>FC máxima estimada</Text>
              <Text style={styles.ptaLinhaValor}>{fcMax.toFixed(0)} bpm</Text>
            </View>
          ) : null}
          {metodo === 'FCR' && fcAlvoMin !== null ? (
            <View style={styles.ptaLinhaDado}>
              <Text style={styles.ptaLinhaLabel}>FC alvo (Karvonen)</Text>
              <Text style={styles.ptaLinhaValor}>{fcAlvoMin} – {fcAlvoMax} bpm</Text>
            </View>
          ) : null}
          {vo2max !== null ? (
            <View style={styles.ptaLinhaDado}>
              <Text style={styles.ptaLinhaLabel}>VO2 máximo (teste)</Text>
              <Text style={styles.ptaLinhaValor}>{vo2max.toFixed(1)} ml/kg/min ({(vo2max / 3.5).toFixed(1)} METs)</Text>
            </View>
          ) : null}
          {vo2Alvo !== null ? (
            <View style={styles.ptaLinhaDado}>
              <Text style={styles.ptaLinhaLabel}>VO2 de treino</Text>
              <Text style={styles.ptaLinhaValor}>{vo2Alvo.toFixed(2)} ml/kg/min</Text>
            </View>
          ) : null}
        </View>
        <View style={styles.ptaCard} wrap={false}>
          <View style={styles.ptaCardHeader}>
            <IconeChama />
            <Text style={styles.ptaCardTitulo}>Gasto energético</Text>
          </View>
          {mets !== null ? (
            <View style={styles.ptaLinhaDado}>
              <Text style={styles.ptaLinhaLabel}>METs de treino</Text>
              <Text style={styles.ptaLinhaValor}>{mets.toFixed(2)}</Text>
            </View>
          ) : null}
          {kcalMin !== null ? (
            <View style={styles.ptaLinhaDado}>
              <Text style={styles.ptaLinhaLabel}>Gasto calórico</Text>
              <Text style={styles.ptaLinhaValor}>{kcalMin.toFixed(2)} kcal/min</Text>
            </View>
          ) : null}
        </View>
      </View>

      {(duracaoMinima !== null || duracaoProposta !== null) && (
        <View style={styles.ptaCardCheio} wrap={false}>
          <View style={styles.ptaCardHeader}>
            <IconeRelogio />
            <Text style={styles.ptaCardTitulo}>Duração da sessão</Text>
          </View>
          <BarraComparacao
            itens={[
              ...(duracaoMinima !== null ? [{ label: 'Mínima sugerida', valor: duracaoMinima, unidade: ' min', cor: '#27ae60' }] : []),
              ...(duracaoProposta !== null ? [{ label: 'Proposta', valor: duracaoProposta, unidade: ' min', cor: '#f94c1a' }] : []),
            ]}
          />
        </View>
      )}

      {pse ? (
        <Text style={styles.cardIndicadorNota}>
          Percepção subjetiva de esforço alvo (escala de Borg): {pse.borg}{pse.descricao ? ` — ${pse.descricao}` : ''}
        </Text>
      ) : null}

      {/* Plano de sessões */}
      {sessoes.length > 0 && (
        <View style={{ marginTop: 6 }}>
          <Text style={[styles.subtituloBloco, { marginBottom: 4 }]}>Plano de sessões</Text>
          <TabelaCampos
            campos={[
              { label: 'Nível', valor: d.nivel },
              { label: 'Número de sessões', valor: numSessoes },
              { label: 'Exercícios', valor: selecionados },
            ]}
          />
          <View style={styles.tabela}>
            <View style={[styles.linhaTabela, styles.linhaTabelaHead]} wrap={false} fixed>
              <Text style={styles.celNum3}>Sessão</Text>
              <Text style={styles.celNum3}>Exercício</Text>
              <Text style={styles.celNum3}>Duração (min)</Text>
            </View>
            {sessoes.map((s, i) => (
              <View key={i} style={[styles.linhaTabela, i % 2 === 1 ? styles.linhaTabelaAlt : null]} wrap={false}>
                <Text style={styles.celNum3}>{s.sessao}</Text>
                <Text style={styles.celNum3}>{s.exercicio}</Text>
                <Text style={styles.celNum3}>{s.tempo !== null ? s.tempo : '—'}</Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
};

const SecaoPrescricaoAerobica = ({ dados, antropometria, vo2Dados }) => {
  const d = dados || {};
  if (d.tipoPrescricao === 'Manual') return <PrescAeroManual d={d} />;
  if (d.tipoPrescricao === 'Automatico') return <PrescAeroAutomatico d={d} antropometria={antropometria} vo2Dados={vo2Dados} />;
  return null;
};

// Mesmos nomes exibidos no sistema (lista de testes da Avaliação Neuromuscular).
const LABEL_TESTE_REP = {
  FlexaoBraco: 'Teste de Flexão de Braços (Força de Membros Superiores)',
  Abdominal: 'Teste de Abdominal (Força de Tronco)',
  Agachamento: 'Teste de Agachamento (Força de Membros Inferiores)',
};

// Faixas do somatório de pontos do teste de força (mesmas cores do formulário).
const FAIXAS_FORCA_1RM = [
  { name: 'Fraca', range: [0, 12], color: '#d70909' },
  { name: 'Satisfatória', range: [12, 24], color: '#ff5e00' },
  { name: 'Média', range: [24, 36], color: '#FFD700' },
  { name: 'Boa', range: [36, 47], color: '#66a3ff' },
  { name: 'Excelente', range: [47, 60], color: '#b3e714' },
];

const fmtNum = (x, casas) => {
  const n = Number(x);
  return Number.isFinite(n) ? n.toFixed(casas) : '—';
};

const BlocoNeuromuscular = ({ dados }) => {
  const faixas = Array.isArray(dados?.faixasSelecionadas) ? dados.faixasSelecionadas : null;
  const resultado = dados?.resultado;

  // Exercícios de força: resultadosExercicios já traz carga, reps, 1RM, força rel. e pontos juntos.
  const exerciciosBrutos = Array.isArray(resultado?.resultadosExercicios) && resultado.resultadosExercicios.length
    ? resultado.resultadosExercicios
    : (Array.isArray(dados?.exerciciosIniciais) ? dados.exerciciosIniciais : []);
  // Só os exercícios efetivamente preenchidos (evita linhas zeradas).
  const exercicios = exerciciosBrutos.filter((ex) => Number(ex?.carga) > 0 && Number(ex?.repeticoes) > 0);

  const reps = Number(dados?.numRepeticoes);
  const apreensao = Number(dados?.forcaApreensaoManual);
  // Preensão bilateral (protocolo novo). Avaliações antigas só têm forcaApreensaoManual.
  const preensao = dados?.preensaoManual || null;
  const melhorD = Number(preensao?.melhorDireita);
  const melhorE = Number(preensao?.melhorEsquerda);
  const temMelhorD = Number.isFinite(melhorD) && melhorD > 0;
  const temMelhorE = Number.isFinite(melhorE) && melhorE > 0;

  const temForca = exercicios.length > 0;
  const temReps = !Number.isNaN(reps) && reps > 0;
  const temApreensao = (!Number.isNaN(apreensao) && apreensao > 0) || temMelhorD || temMelhorE;
  const classePreensao = temApreensao ? classificar(apreensao, faixas) : null;
  const textoPreensao = textoClassificacaoPreensao(classePreensao);
  const assimetriaPreensao = preensao ? descreverAssimetria(preensao) : null;
  const tentativa = (lista, i) => {
    const n = Number(String(lista?.[i] ?? '').replace(',', '.'));
    return Number.isFinite(n) && n > 0 ? n : '—';
  };

  if (!temForca && !temReps && !temApreensao) {
    // Nada reconhecível preenchido: mostra o mínimo disponível.
    return (
      <TabelaCampos
        campos={[
          { label: 'Tipo de teste', valor: dados?.tipoTeste },
          { label: 'Método', valor: dados?.metodo },
        ]}
      />
    );
  }

  return (
    <View>
      {temForca && (
        <View style={styles.cardIndicador}>
          <CabecalhoIndicador
            titulo="Teste de Força Geral"
            valor={v(resultado?.totalPontos)} unidade="pontos"
            classe={resultado?.classificacao} faixas={FAIXAS_FORCA_1RM} valorFaixa={Number(resultado?.totalPontos)}
          />
          <View style={styles.tabela}>
            <View style={[styles.linhaTabela, styles.linhaTabelaHead]} wrap={false} fixed>
              <Text style={styles.celExerc}>Exercício</Text>
              <Text style={styles.celNum}>Carga</Text>
              <Text style={styles.celNum}>Reps</Text>
              <Text style={styles.celNum}>1RM</Text>
              <Text style={styles.celNum}>F. Rel.</Text>
              <Text style={styles.celNum}>Pontos</Text>
            </View>
            {exercicios.map((ex, i) => (
              <View key={i} style={[styles.linhaTabela, i % 2 === 1 ? styles.linhaTabelaAlt : null]} wrap={false}>
                <Text style={styles.celExerc}>{v(ex?.nome ?? ex?.id)}</Text>
                <Text style={styles.celNum}>{v(ex?.carga)}</Text>
                <Text style={styles.celNum}>{v(ex?.repeticoes)}</Text>
                <Text style={styles.celNum}>{fmtNum(ex?.oneRM, 1)}</Text>
                <Text style={styles.celNum}>{fmtNum(ex?.forcaRelativa, 2)}</Text>
                <Text style={styles.celNum}>{v(ex?.pontos)}</Text>
              </View>
            ))}
          </View>
          <View style={{ marginTop: 6 }}>
            <BarraFaixas valor={Number(resultado?.totalPontos)} faixas={FAIXAS_FORCA_1RM} />
          </View>
        </View>
      )}

      {temReps && (
        <View style={styles.cardIndicador}>
          <CabecalhoIndicador
            titulo={LABEL_TESTE_REP[dados?.metodo] || 'Teste de repetições máximas'}
            valor={reps} unidade="repetições"
            classe={classificar(reps, faixas)} faixas={faixas}
          />
          {faixas ? <BarraFaixas valor={reps} faixas={faixas} /> : null}
        </View>
      )}

      {temApreensao && (
        <View style={styles.cardIndicador}>
          <CabecalhoIndicador
            titulo="Teste de Preensão Manual (Dinamometria)"
            valor={apreensao > 0 ? apreensao : v(null)}
            unidade="kgf"
            classe={classePreensao}
            faixas={faixas}
            valorFaixa={apreensao}
          />
          {faixas ? <BarraFaixas valor={apreensao} faixas={faixas} /> : null}

          {preensao && (temMelhorD || temMelhorE) && (
            <View style={{ marginTop: 6 }}>
              <View style={styles.tabela}>
                <View style={[styles.linhaTabela, styles.linhaTabelaHead]} wrap={false} fixed>
                  <Text style={styles.celExerc}>Tentativa</Text>
                  <Text style={styles.celNum}>Mão direita (kgf)</Text>
                  <Text style={styles.celNum}>Mão esquerda (kgf)</Text>
                </View>
                {[0, 1, 2].map((i) => (
                  <View key={i} style={[styles.linhaTabela, i % 2 === 1 ? styles.linhaTabelaAlt : null]} wrap={false}>
                    <Text style={styles.celExerc}>{`${i + 1}ª`}</Text>
                    <Text style={styles.celNum}>{tentativa(preensao.direita, i)}</Text>
                    <Text style={styles.celNum}>{tentativa(preensao.esquerda, i)}</Text>
                  </View>
                ))}
                <View style={[styles.linhaTabela, styles.linhaTabelaAlt]} wrap={false}>
                  <Text style={[styles.celExerc, { fontWeight: 'bold' }]}>Melhor valor</Text>
                  <Text style={[styles.celNum, { fontWeight: 'bold' }]}>{temMelhorD ? melhorD : '—'}</Text>
                  <Text style={[styles.celNum, { fontWeight: 'bold' }]}>{temMelhorE ? melhorE : '—'}</Text>
                </View>
              </View>
              <Text style={styles.cardIndicadorNota}>
                {`Mão dominante: ${preensao.maoDominante === 'Esquerda' ? 'esquerda' : 'direita'}. A classificação considera o melhor valor da mão dominante.`}
                {assimetriaPreensao ? ` ${assimetriaPreensao}` : ''}
              </Text>
            </View>
          )}

          <View style={{ marginTop: 8 }}>
            <Text style={styles.anamneseObsTexto}><Text style={{ fontWeight: 'bold' }}>Protocolo: </Text>{PROTOCOLO_PREENSAO_MANUAL}</Text>
            <Text style={[styles.anamneseObsTexto, { marginTop: 4 }]}><Text style={{ fontWeight: 'bold' }}>Observação: </Text>{OBSERVACAO_PREENSAO_MANUAL}</Text>
            {textoPreensao && (
              <>
                <Text style={[styles.anamneseObsTexto, { marginTop: 6 }]}><Text style={{ fontWeight: 'bold' }}>{`Classificação ${String(classePreensao).toUpperCase()}: `}</Text>{textoPreensao.introducao}</Text>
                <Text style={[styles.anamneseObsTexto, { marginTop: 4 }]}>{textoPreensao.resultado}</Text>
                <Text style={[styles.anamneseObsTexto, { marginTop: 4 }]}><Text style={{ fontWeight: 'bold' }}>Recomendações: </Text>{textoPreensao.recomendacoes}</Text>
              </>
            )}
          </View>
        </View>
      )}
    </View>
  );
};

// A avaliação pode conter mais de um teste neuromuscular; cada um vira um bloco.
const SecaoNeuromuscular = ({ dados }) => {
  const testes = Array.isArray(dados?.testesRealizados) && dados.testesRealizados.length > 0
    ? dados.testesRealizados
    : [dados];
  return (
    <View>
      {testes.map((teste, i) => (
        <View key={i} style={i > 0 ? { marginTop: 10 } : null}>
          <BlocoNeuromuscular dados={teste} />
        </View>
      ))}
    </View>
  );
};

const SecaoPrescricaoForca = ({ dados }) => {
  const treinos = Array.isArray(dados?.treinos) ? dados.treinos : [];
  return (
    <View>
      <TabelaCampos
        campos={[
          { label: 'Nº de Treinos', valor: dados?.numTreinos },
          { label: 'Quantidade Total', valor: dados?.qtdTreinosTotal },
          { label: 'Data Inicial', valor: formatDate(dados?.dataInicial) },
          { label: 'Dias dos Treinos', valor: dados?.diasTreinos },
        ]}
      />
      {treinos.length > 0 && (
        <View style={{ marginTop: 8 }}>
          <Text style={styles.subtituloBloco}>Treinos</Text>
          {treinos.map((t, i) => (
            <View key={i} style={styles.blocoTreino} wrap={false}>
              <Text style={styles.tituloTreino}>Treino {i + 1}</Text>
              <Text style={styles.descricaoTreino}>{v(t?.nome || t?.titulo)}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

const SecaoSimples = ({ dados }) => {
  const entries = Object.entries(dados || {}).filter(([k]) => k !== 'lastUpdateDate' && k !== 'evaluationDate');
  if (entries.length === 0) return null;
  return (
    <TabelaCampos
      campos={entries.map(([k, val]) => ({ label: k, valor: val }))}
    />
  );
};

// --- Escalas de idosos: total e classificação recomputados a partir das respostas ---

const FAIXAS_KATZ = [
  { name: 'Dependência importante', range: [0, 2], color: '#d70909' },
  { name: 'Dependência moderada', range: [3, 5], color: '#ffbb28' },
  { name: 'Independente', range: [6, 6], color: '#00c457' },
];
const FAIXAS_LAWTON = [
  { name: 'Dependência grave', range: [9, 18], color: '#d70909' },
  { name: 'Dependência parcial', range: [19, 26], color: '#ffbb28' },
  { name: 'Independência total', range: [27, 27], color: '#00c457' },
];
const FAIXAS_BERG = [
  { name: 'Alto risco de queda', range: [0, 20], color: '#d70909' },
  { name: 'Risco médio', range: [21, 40], color: '#ffbb28' },
  { name: 'Baixo risco', range: [41, 56], color: '#00c457' },
];
const FAIXAS_CAP_FUNCIONAL = [
  { name: 'Baixo', range: [0, 6], color: '#d70909' },
  { name: 'Moderado', range: [7, 11], color: '#ffbb28' },
  { name: 'Avançado', range: [12, 12], color: '#00c457' },
];

// Painel de pontuação: total em destaque, selo colorido da classificação e régua das faixas.
const CardPontuacao = ({ pontos, total, rotulo, classificacao, faixas }) => {
  const faixa = faixas.find((f) => pontos >= f.range[0] && pontos <= f.range[1]);
  const cor = faixa?.color || COR_TEXTO;
  return (
    <View style={[styles.coronarioPainel, { borderColor: cor }]} wrap={false}>
      <View style={styles.coronarioPainelTopo}>
        <Text style={styles.coronarioPontos}>
          {pontos}
          <Text style={styles.coronarioPontosLabel}> de {total} {rotulo || 'pontos'}</Text>
        </Text>
        {classificacao ? (
          <View style={[styles.coronarioBadge, { backgroundColor: cor }]}>
            <Text style={styles.coronarioBadgeTxt}>{classificacao}</Text>
          </View>
        ) : null}
      </View>
      <BarraFaixas valor={pontos} faixas={faixas} />
    </View>
  );
};

const SecaoKatz = ({ dados }) => {
  const respostas = normalizarRespostas(dados?.respostas ?? dados);
  if (respostas.length === 0) return null;
  const pont = respostas.reduce((acc, r) => (r.valor === 'Sem ajuda' ? acc + 1 : acc), 0);
  const incompleto = respostas.some((r) => !r.valor);
  const classe = incompleto ? null
    : pont === 6 ? 'Independente' : pont >= 3 ? 'Dependência Moderada' : 'Dependência Importante';
  return (
    <View>
      <CardPontuacao pontos={pont} total={6} rotulo="atividades independentes" classificacao={classe} faixas={FAIXAS_KATZ} />
      <RespostasLabeladas respostas={respostas} labels={LABELS_KATZ} />
    </View>
  );
};

const SecaoLawton = ({ dados }) => {
  const respostas = normalizarRespostas(dados?.respostas ?? dados);
  if (respostas.length === 0) return null;
  const MAPA = { 'Sem ajuda': 3, 'Ajuda parcial': 2, 'Ajuda total': 1 };
  const total = respostas.reduce((acc, r) => acc + (MAPA[r.valor] || 0), 0);
  const incompleto = respostas.some((r) => !r.valor);
  const classe = incompleto ? null
    : total >= 27 ? 'Independência Total' : total >= 19 ? 'Dependência Parcial' : 'Dependência Grave';
  return (
    <View>
      <CardPontuacao pontos={total} total={27} classificacao={classe} faixas={FAIXAS_LAWTON} />
      <RespostasLabeladas respostas={respostas} labels={LABELS_LAWTON} />
    </View>
  );
};

const SecaoBerg = ({ dados }) => {
  const respostas = normalizarRespostas(dados?.respostas ?? dados);
  if (respostas.length === 0) return null;
  const total = respostas.reduce((acc, r) => acc + (Number(r.valor) || 0), 0);
  const classe = total >= 41 ? 'Independente' : total >= 21 ? 'Auxílio para deambular' : 'Cadeirante';
  return (
    <View>
      <CardPontuacao pontos={total} total={56} classificacao={classe} faixas={FAIXAS_BERG} />
      <RespostasLabeladas respostas={respostas} labels={LABELS_BERG} />
    </View>
  );
};

// --- Bateria de testes físicos de capacidade funcional (Rikli & Jones) ---

// Gate da página: pelo menos um teste com resultado numérico registrado.
const temTesteCF = (d) => {
  if (!d) return false;
  const num = (x) => {
    const n = Number(String(x).replace(',', '.'));
    return Number.isFinite(n) && String(x ?? '').trim() !== '' ? n : null;
  };
  const c = d.caminhada6min || {};
  return TESTES_CF.some((t) => (t.chave === 'caminhada6min'
    ? num(c.distanciaTeste1) !== null || num(c.distanciaTeste2) !== null
    : num(d[t.chave]) !== null));
};

const SecaoTesteCapacidadeFuncional = ({ dados, aluno }) => {
  const d = dados || {};
  const c = d.caminhada6min || {};
  const sexo = aluno?.sexo;
  const idade = aluno?.idade;

  const num = (x) => {
    const n = Number(String(x).replace(',', '.'));
    return Number.isFinite(n) && String(x ?? '').trim() !== '' ? n : null;
  };

  const valorDoTeste = (chave) => {
    if (chave !== 'caminhada6min') return num(d[chave]);
    // Vale o melhor desempenho entre as duas tentativas.
    const vals = [c.distanciaTeste1, c.distanciaTeste2].map(num).filter((x) => x !== null && x > 0);
    return vals.length ? Math.max(...vals) : null;
  };

  const linhas = TESTES_CF
    .map((teste) => ({ teste, valor: valorDoTeste(teste.chave) }))
    .filter((x) => x.valor !== null);

  if (linhas.length === 0) return null;

  const coletas = [
    { rotulo: 'Primeiro teste', horario: c.horarioTeste1, borg: c.repousoBorg, fr: c.repousoFR, sp0: c.repousoSp0, fc: c.repousoFC, pa: c.repousoPA, distancia: c.distanciaTeste1, paradas: c.paradas1 },
    { rotulo: 'Segundo teste', horario: c.horarioTeste2, borg: c.repousoBorg2, fr: c.repousoFR2, sp0: c.repousoSp02, fc: c.repousoFC2, pa: c.repousoPA2, distancia: c.distanciaTeste2, paradas: c.paradas2 },
  ].filter(({ rotulo, ...campos }) => temConteudo(campos));

  return (
    <View>
      <View style={styles.tabela}>
        <View style={[styles.linhaTabela, styles.linhaTabelaHead]} wrap={false} fixed>
          <Text style={styles.celTesteCF}>Teste</Text>
          <Text style={styles.celNum3}>Resultado</Text>
          <Text style={styles.celNum3}>Esperado</Text>
          <Text style={styles.celTesteCFClasse}>Classificação</Text>
        </View>
        {linhas.map(({ teste, valor }, i) => {
          const classe = classificarCF(teste, valor, sexo, idade);
          const faixa = referenciaCF(teste, sexo, idade);
          return (
            <View key={i} style={[styles.linhaTabela, i % 2 === 1 ? styles.linhaTabelaAlt : null]} wrap={false}>
              <Text style={styles.celTesteCF}>{teste.nome}</Text>
              <Text style={styles.celNum3}>{valor} {teste.unidade}</Text>
              <Text style={styles.celNum3}>{faixa ? formatarFaixaCF(faixa) : '—'}</Text>
              <Text style={[styles.celTesteCFClasse, classe ? { color: CORES_CLASSIFICACAO_CF[classe], fontWeight: 'bold' } : null]}>
                {classe || '—'}
              </Text>
            </View>
          );
        })}
      </View>
      <Text style={styles.cardIndicadorNota}>
        Faixas de referência por sexo e idade (Rikli &amp; Jones). O teste de levantar e caminhar é medido em
        segundos, portanto tempos menores indicam melhor desempenho.
      </Text>

      {coletas.length > 0 && (
        <View style={{ marginTop: 8 }}>
          <Text style={[styles.subtituloBloco, { marginBottom: 4 }]}>Caminhada de 6 minutos — coletas em repouso</Text>
          <View style={styles.tabela}>
            <View style={[styles.linhaTabela, styles.linhaTabelaHead]} wrap={false} fixed>
              <Text style={styles.celNum3}>Tentativa</Text>
              <Text style={styles.celNum3}>Horário</Text>
              <Text style={styles.celNum3}>Borg</Text>
              <Text style={styles.celNum3}>FR</Text>
              <Text style={styles.celNum3}>Sp0</Text>
              <Text style={styles.celNum3}>FC</Text>
              <Text style={styles.celNum3}>PA</Text>
              <Text style={styles.celNum3}>Distância</Text>
              <Text style={styles.celNum3}>Paradas</Text>
            </View>
            {coletas.map((t, i) => (
              <View key={i} style={[styles.linhaTabela, i % 2 === 1 ? styles.linhaTabelaAlt : null]} wrap={false}>
                <Text style={styles.celNum3}>{t.rotulo}</Text>
                <Text style={styles.celNum3}>{v(t.horario)}</Text>
                <Text style={styles.celNum3}>{v(t.borg)}</Text>
                <Text style={styles.celNum3}>{v(t.fr)}</Text>
                <Text style={styles.celNum3}>{v(t.sp0)}</Text>
                <Text style={styles.celNum3}>{v(t.fc)}</Text>
                <Text style={styles.celNum3}>{v(t.pa)}</Text>
                <Text style={styles.celNum3}>{t.distancia ? `${t.distancia} m` : '—'}</Text>
                <Text style={styles.celNum3}>{v(t.paradas)}</Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
};

const SecaoCapacidadeFuncional = ({ dados }) => {
  const respostas = normalizarRespostas(dados?.respostas ?? dados);
  if (respostas.length === 0) return null;
  const MAPA = { 'Realiza sem dificuldade': 1, 'Realiza com dificuldade': 0, 'Não realiza': -1 };
  const pont = respostas.reduce((acc, r) => (MAPA[r.valor] === 1 ? acc + 1 : acc), 0);
  const incapazes = respostas.reduce((acc, r) => (MAPA[r.valor] === -1 ? acc + 1 : acc), 0);
  const incompleto = respostas.some((r) => !r.valor);
  const classe = incompleto ? null
    : (pont <= 6 || incapazes >= 3) ? 'Baixo' : pont >= 12 ? 'Avançado' : 'Moderado';
  return (
    <View>
      <CardPontuacao pontos={pont} total={12} rotulo="atividades sem dificuldade" classificacao={classe} faixas={FAIXAS_CAP_FUNCIONAL} />
      <Text style={styles.cardIndicadorNota}>
        Atividades que o avaliado não realiza: {incapazes}. Três ou mais itens não realizados classificam o nível como baixo.
      </Text>
      <RespostasLabeladas respostas={respostas} labels={LABELS_CAP_FUNCIONAL} />
    </View>
  );
};

// ---------- Seções disponíveis (usado pelo modal de geração do relatório) ----------

// Ordem das páginas do relatório e critério de "etapa realizada" de cada uma.
export const SECOES_RELATORIO = [
  { chave: 'Anamnese', nome: 'Anamnese', disponivel: (e) => temConteudo(e.anamnesis) },
  { chave: 'ParQ', nome: 'PAR-Q', disponivel: (e) => temConteudo(e.parq ?? e.parQ) },
  { chave: 'Coronariano', nome: 'Risco Coronariano', disponivel: (e) => temConteudo(e.coronariano) },
  { chave: 'Antropometria', nome: 'Antropometria', disponivel: (e) => temConteudo(e.antropometria) },
  { chave: 'ComposicaoCorporal', nome: 'Composição Corporal', disponivel: (e) => temConteudo(e.composicaoCorporal) },
  { chave: 'Vo2', nome: 'VO2 máx', disponivel: (e) => temResultadoVo2(e.vo2) },
  { chave: 'PrescricaoTreinoAerobico', nome: 'Prescrição do Treinamento Aeróbico', disponivel: (e) => temConteudoPrescAero(e.prescricaoAerobico) },
  { chave: 'AvaliacaoNeuromuscular', nome: 'Avaliação Neuromuscular', disponivel: (e) => temConteudo(e.avaliacaoNeuromuscular) },
  { chave: 'PrescricaoTreinoForca', nome: 'Prescrição do Treinamento de Força', oculta: true, disponivel: (e) => temConteudo(e.prescricaoForca) },
  { chave: 'CapFuncionalQuest', nome: 'Classificação Funcional', apenasIdoso: true, disponivel: (e) => temConteudo(e.capacidadeFuncional) },
  { chave: 'TesteCapFunc', nome: 'Teste de Capacidade Funcional', apenasIdoso: true, disponivel: (e) => temTesteCF(e.testeCapacidadeFuncional) },
  { chave: 'Katz', nome: 'Índice de Katz', apenasIdoso: true, disponivel: (e) => temConteudo(e.katz) },
  { chave: 'Lawton', nome: 'Escala de Lawton', apenasIdoso: true, disponivel: (e) => temConteudo(e.lawton) },
  { chave: 'EscalaBerg', nome: 'Escala de Berg', apenasIdoso: true, disponivel: (e) => temConteudo(e.escalaBerg ?? e.escala_berg) },
  // Página final gerada a partir do conjunto da avaliação (IA com fallback por regras).
  { chave: 'AnaliseIA', nome: 'Análise e recomendações (IA)', disponivel: (e) => temConteudo(e.antropometria) || temConteudo(e.composicaoCorporal) || temResultadoVo2(e.vo2) || temConteudo(e.avaliacaoNeuromuscular) },
];

// As etapas exclusivas de idosos só entram na lista para esse perfil — ou quando, por algum
// motivo, já existe dado salvo nelas (nesse caso, esconder impediria de incluí-las no relatório).
export const secoesDisponiveis = (evaluationData, perfil) => {
  const e = evaluationData || {};
  return SECOES_RELATORIO
    .map(({ chave, nome, apenasIdoso, oculta, disponivel }) => ({ chave, nome, apenasIdoso, oculta, disponivel: disponivel(e) }))
    // Etapas fora do fluxo (Prescrição de Força) e as exclusivas de idoso só aparecem
    // quando já existe dado salvo nelas — esconder impediria de incluí-las no relatório.
    .filter((s) => !s.oculta || s.disponivel)
    .filter((s) => !s.apenasIdoso || perfil === 'idosos' || s.disponivel)
    .map(({ chave, nome, disponivel }) => ({ chave, nome, disponivel }));
};

// ---------- Página utilitária ----------

const PaginaSecao = ({ personal, aluno, evaluationData, titulo, geradoEm, children }) => (
  <Page size="A4" style={styles.page}>
    <MolduraBorda />
    <Header personal={personal} />
    <BlocoTituloAluno titulo={titulo} aluno={aluno} evaluationData={evaluationData} />
    <View style={styles.conteudoSecao}>{children}</View>
    <Footer geradoEm={geradoEm} />
  </Page>
);

// ---------- Função principal ----------

// Página "Análise e recomendações": resumo do conjunto da avaliação e sugestões de treino.
const SecaoAnaliseIA = ({ resumo }) => {
  if (!resumo) return null;
  const blocos = [
    { titulo: 'Resumo da avaliação', texto: resumo.resumo },
    { titulo: 'Sugestões para emagrecimento', texto: resumo.emagrecimento },
    { titulo: 'Sugestões para hipertrofia', texto: resumo.hipertrofia },
  ].filter((b) => typeof b.texto === 'string' && b.texto.trim() !== '');
  if (blocos.length === 0) return null;
  return (
    <View>
      {blocos.map((b, i) => (
        <View key={i} style={styles.anamneseObs}>
          <Text style={styles.anamneseObsTitulo}>{b.titulo}</Text>
          <Text style={styles.anamneseObsTexto}>{b.texto}</Text>
        </View>
      ))}
      <Text style={[styles.cardIndicadorNota, { marginTop: 10 }]}>
        Texto gerado por inteligência artificial a partir dos dados desta avaliação e revisado pelo profissional responsável. Não substitui avaliação médica.
      </Text>
    </View>
  );
};

export function gerarRelatorio({ evaluationData, aluno, personal, secoes, resumoIA }) {
  const e = evaluationData || {};
  const geradoEm = new Date();

  // Sem seleção explícita, entram todas as seções preenchidas (comportamento padrão do relatório).
  const incluir = (chave) => !Array.isArray(secoes) || secoes.includes(chave);

  return (
    <Document>
      {/* Capa — sem header/footer (R5 / R7) */}
      <Page size="A4" style={styles.capa}>
        <MolduraBorda />
        <Text style={styles.capaTitulo}>LAUDO DE{'\n'}AVALIAÇÃO FÍSICA</Text>
        <View style={styles.capaLogoArea}>
          {personal?.photo ? <Image src={personal.photo} style={styles.logoCapa} /> : null}
        </View>
        <View style={styles.capaRodape}>
          <View style={styles.capaRodapeEsq}>
            <Text style={styles.capaRodapeNome}>{v(aluno?.name)}</Text>
            <Text style={styles.capaRodapeTxt}>Data: {formatDate(e.evaluationDate)}</Text>
          </View>
          <View style={styles.capaRodapeDir}>
            <Text style={styles.capaRodapeNome}>{v(personal?.name)}</Text>
            <Text style={styles.capaRodapeTxt}>{v(personal?.email)}</Text>
            {personal?.cref ? <Text style={styles.capaRodapeTxt}>CREF: {personal.cref}</Text> : null}
          </View>
        </View>
      </Page>

      {incluir('Anamnese') && temConteudo(e.anamnesis) && (
        <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="Anamnese" geradoEm={geradoEm}>
          <SecaoAnamnese dados={e.anamnesis} perfil={aluno?.profile} observacoes={e.observacoesAnamnese} />
        </PaginaSecao>
      )}

      {incluir('ParQ') && temConteudo(e.parq ?? e.parQ) && (
        <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="PAR-Q" geradoEm={geradoEm}>
          <SecaoParQ dados={e.parq ?? e.parQ} dataAvaliacao={e.evaluationDate} />
          <Text style={styles.notaRodape}>
            Se apenas uma das questões for respondida com um "sim", seria recomendado uma avaliação de um médico antes do início do programa.
          </Text>
        </PaginaSecao>
      )}

      {incluir('Coronariano') && temConteudo(e.coronariano) && (
        <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="Avaliação de Risco Coronariano" geradoEm={geradoEm}>
          <SecaoCoronariana dados={e.coronariano} />
        </PaginaSecao>
      )}

      {incluir('Antropometria') && temConteudo(e.antropometria) && (
        <>
          <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="Antropometria — Perímetros Corporais" geradoEm={geradoEm}>
            <SecaoAntropometriaPerimetros dados={e.antropometria} sexo={aluno?.sexo} />
          </PaginaSecao>
          <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="Antropometria — Indicadores Antropométricos" geradoEm={geradoEm}>
            <SecaoAntropometriaIndices dados={e.antropometria} sexo={aluno?.sexo} />
          </PaginaSecao>
          <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="Antropometria — Valores de Referência" geradoEm={geradoEm}>
            <SecaoValoresReferencia dados={e.antropometria} />
          </PaginaSecao>
        </>
      )}

      {incluir('ComposicaoCorporal') && temConteudo(e.composicaoCorporal) && (
        <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="Análise da Composição Corporal" geradoEm={geradoEm}>
          <SecaoComposicaoCorporal dados={e.composicaoCorporal} antropometria={e.antropometria} sexo={aluno?.sexo} />
        </PaginaSecao>
      )}

      {incluir('Vo2') && temResultadoVo2(e.vo2) && (
        <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="Análise da Capacidade Cardiorrespiratória" geradoEm={geradoEm}>
          <SecaoVo2 dados={e.vo2} antropometria={e.antropometria} sexo={aluno?.sexo} idade={aluno?.idade} />
        </PaginaSecao>
      )}

      {incluir('PrescricaoTreinoAerobico') && temConteudoPrescAero(e.prescricaoAerobico) && (
        <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="Prescrição de Treino Aeróbico" geradoEm={geradoEm}>
          <SecaoPrescricaoAerobica dados={e.prescricaoAerobico} antropometria={e.antropometria} vo2Dados={e.vo2} />
        </PaginaSecao>
      )}

      {incluir('AvaliacaoNeuromuscular') && temConteudo(e.avaliacaoNeuromuscular) && (
        <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="Avaliação Neuromuscular" geradoEm={geradoEm}>
          <SecaoNeuromuscular dados={e.avaliacaoNeuromuscular} />
        </PaginaSecao>
      )}

      {incluir('PrescricaoTreinoForca') && temConteudo(e.prescricaoForca) && (
        <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="Prescrição de Força" geradoEm={geradoEm}>
          <SecaoPrescricaoForca dados={e.prescricaoForca} />
        </PaginaSecao>
      )}

      {incluir('CapFuncionalQuest') && temConteudo(e.capacidadeFuncional) && (
        <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="Classificação Funcional" geradoEm={geradoEm}>
          <SecaoCapacidadeFuncional dados={e.capacidadeFuncional} />
        </PaginaSecao>
      )}

      {incluir('TesteCapFunc') && temTesteCF(e.testeCapacidadeFuncional) && (
        <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="Teste de Capacidade Funcional" geradoEm={geradoEm}>
          <SecaoTesteCapacidadeFuncional dados={e.testeCapacidadeFuncional} aluno={aluno} />
        </PaginaSecao>
      )}

      {incluir('Katz') && temConteudo(e.katz) && (
        <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="Índice de Katz" geradoEm={geradoEm}>
          <SecaoKatz dados={e.katz} />
        </PaginaSecao>
      )}

      {incluir('Lawton') && temConteudo(e.lawton) && (
        <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="Escala de Lawton" geradoEm={geradoEm}>
          <SecaoLawton dados={e.lawton} />
        </PaginaSecao>
      )}

      {incluir('EscalaBerg') && temConteudo(e.escalaBerg ?? e.escala_berg) && (
        <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="Escala de Berg" geradoEm={geradoEm}>
          <SecaoBerg dados={e.escalaBerg ?? e.escala_berg} />
        </PaginaSecao>
      )}

      {incluir('AnaliseIA') && resumoIA && (
        <PaginaSecao personal={personal} aluno={aluno} evaluationData={e} titulo="Análise e Recomendações" geradoEm={geradoEm}>
          <SecaoAnaliseIA resumo={resumoIA} />
        </PaginaSecao>
      )}
    </Document>
  );
}

// Alias para compatibilidade com chamadas legadas (Home_aluno usava gerarHTMLRelatorio).
export const gerarHTMLRelatorio = gerarRelatorio;

// ---------- Relatório comparativo (evolução entre 2 a 4 avaliações) ----------

// Zero é o valor padrão de campo não preenchido nos formulários: não entra na comparação.
const numCmp = (x) => {
  if (x === undefined || x === null || String(x).trim() === '') return null;
  const n = Number(String(x).replace(',', '.'));
  return Number.isFinite(n) && n !== 0 ? n : null;
};

const intervaloEntreDatas = (isoA, isoB) => {
  const a = new Date(isoA);
  const b = new Date(isoB);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const dias = Math.abs(Math.round((b - a) / 86400000));
  if (dias < 31) return `${dias} dia${dias === 1 ? '' : 's'}`;
  const meses = Math.round(dias / 30.44);
  if (meses < 12) return `${meses} mês${meses === 1 ? '' : 'es'}`;
  const anos = Math.floor(meses / 12);
  const resto = meses % 12;
  const parteAnos = `${anos} ano${anos === 1 ? '' : 's'}`;
  return resto === 0 ? parteAnos : `${parteAnos} e ${resto} mês${resto === 1 ? '' : 'es'}`;
};

// Linha da comparação: um valor por avaliação (null = não medido naquela avaliação).
// `sentido` marca o que é melhora ('maior' ou 'menor'); sem ele, a variação fica neutra.
const linha = (label, valores, extras = {}) => ({ label, valores, ...extras });
const temLinhaPreenchida = (linhas) => linhas.some((l) => l.valores.some((x) => x !== null));

const primeiroUltimo = (valores) => {
  const validos = valores.filter((x) => x !== null);
  return validos.length >= 2 ? [validos[0], validos[validos.length - 1]] : null;
};

// Tabela de evolução: valor em cada avaliação e a variação entre a primeira e a última medidas.
const TabelaComparativa = ({ linhas, datas }) => {
  const validas = linhas.filter((l) => l.valores.some((x) => x !== null));
  if (validas.length === 0) return null;

  return (
    <View style={styles.tabela}>
      <View style={[styles.linhaTabela, styles.linhaTabelaHead]} wrap={false} fixed>
        <Text style={styles.celCompLabel}>Indicador</Text>
        {datas.map((d, i) => <Text key={i} style={styles.celCompValor}>{d}</Text>)}
        <Text style={styles.celCompVar}>Variação</Text>
      </View>
      {validas.map((l, i) => {
        const casas = l.casas ?? 1;
        const par = primeiroUltimo(l.valores);
        const delta = par ? par[1] - par[0] : null;
        const perc = par && par[0] !== 0 ? (delta / Math.abs(par[0])) * 100 : null;
        const melhorou = (delta === null || delta === 0 || !l.sentido)
          ? null
          : (l.sentido === 'maior' ? delta > 0 : delta < 0);
        const cor = melhorou === null ? COR_TEXTO_SUAVE : (melhorou ? '#00a651' : COR_DESTAQUE);
        const sinal = delta > 0 ? '+' : '';
        return (
          <View key={i} style={[styles.linhaTabela, i % 2 === 1 ? styles.linhaTabelaAlt : null]} wrap={false}>
            <Text style={styles.celCompLabel}>{l.label}</Text>
            {l.valores.map((x, j) => (
              <Text key={j} style={styles.celCompValor}>
                {x !== null ? `${fmtNum(x, casas)}${l.unidade ? ` ${l.unidade}` : ''}` : '—'}
              </Text>
            ))}
            <Text style={[styles.celCompVar, { color: cor, fontWeight: melhorou === null ? 'normal' : 'bold' }]}>
              {delta === null ? '—' : `${sinal}${fmtNum(delta, casas)}${perc !== null ? ` (${sinal}${fmtNum(perc, 1)}%)` : ''}`}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

// Gráfico de linha de um indicador ao longo das avaliações (SVG desenhado no PDF).
const GraficoLinha = ({ titulo, datas, valores, unidade, casas = 1, largura = 236, altura = 118 }) => {
  const pontos = valores.map((v, i) => ({ i, v })).filter((p) => p.v !== null);
  if (pontos.length === 0) return null;

  const mE = 30, mD = 12, mT = 16, mB = 20; // margens: esquerda, direita, topo, base
  const w = largura - mE - mD;
  const h = altura - mT - mB;
  const n = datas.length;
  const xDe = (i) => mE + (n === 1 ? w / 2 : (i / (n - 1)) * w);

  const vals = pontos.map((p) => p.v);
  let min = Math.min(...vals);
  let max = Math.max(...vals);
  if (min === max) { min -= Math.abs(min) * 0.1 || 1; max += Math.abs(max) * 0.1 || 1; }
  const folga = (max - min) * 0.15;
  min -= folga; max += folga;
  const yDe = (v) => mT + h - ((v - min) / (max - min)) * h;

  const caminho = pontos.map((p) => `${xDe(p.i).toFixed(1)},${yDe(p.v).toFixed(1)}`).join(' ');
  const linhasGrade = [0, 0.5, 1].map((f) => mT + h * f);

  return (
    <View style={styles.graficoLinhaCaixa} wrap={false}>
      <Text style={styles.graficoLinhaTitulo}>{titulo}{unidade ? ` (${unidade})` : ''}</Text>
      <Svg width={largura} height={altura}>
        {linhasGrade.map((y, k) => (
          <Line key={k} x1={mE} y1={y} x2={mE + w} y2={y} stroke="#e5e7eb" strokeWidth={0.6} />
        ))}
        <Text x={mE - 3} y={mT + 3} fill={COR_TEXTO_SUAVE} fontSize={6} textAnchor="end">{fmtNum(max - folga, casas)}</Text>
        <Text x={mE - 3} y={mT + h + 2} fill={COR_TEXTO_SUAVE} fontSize={6} textAnchor="end">{fmtNum(min + folga, casas)}</Text>
        {pontos.length > 1 ? (
          <Polyline points={caminho} fill="none" stroke={COR_DESTAQUE} strokeWidth={1.6} />
        ) : null}
        {pontos.map((p) => (
          <G key={p.i}>
            <Circle cx={xDe(p.i)} cy={yDe(p.v)} r={2.6} fill="#ffffff" stroke={COR_DESTAQUE} strokeWidth={1.4} />
            <Text x={xDe(p.i)} y={yDe(p.v) - 5} fill={COR_TEXTO} fontSize={6.5} textAnchor="middle">{fmtNum(p.v, casas)}</Text>
          </G>
        ))}
        {datas.map((d, i) => (
          <Text key={i} x={xDe(i)} y={altura - 6} fill={COR_TEXTO_SUAVE} fontSize={6} textAnchor="middle">{d}</Text>
        ))}
      </Svg>
    </View>
  );
};

// Grade de gráficos, dois por linha.
const GradeGraficos = ({ graficos, datas }) => {
  const visiveis = graficos.filter((g) => g.valores.some((x) => x !== null));
  if (visiveis.length === 0) return null;
  const pares = [];
  for (let i = 0; i < visiveis.length; i += 2) pares.push(visiveis.slice(i, i + 2));
  return (
    <View style={{ marginTop: 8 }}>
      {pares.map((par, i) => (
        <View key={i} style={[styles.graficoLinhaLinha, i === pares.length - 1 ? { marginBottom: 0 } : null]}>
          {par.map((g, j) => (
            <View key={j} style={[styles.graficoLinhaCelula, j === 0 && par.length > 1 ? { marginRight: 8 } : null]}>
              <GraficoLinha titulo={g.titulo} datas={datas} valores={g.valores} unidade={g.unidade} casas={g.casas} />
            </View>
          ))}
          {par.length === 1 ? <View style={styles.graficoLinhaCelula} /> : null}
        </View>
      ))}
    </View>
  );
};

// ---- Linhas por seção: cada função recebe a lista de avaliações (ordem cronológica) ----

const colunas = (avaliacoes, chaveSecao, campo) => avaliacoes.map((e) => numCmp(e?.[chaveSecao]?.[campo]));

const linhasAntropometriaCmp = (avs) => [
  linha('Peso', colunas(avs, 'antropometria', 'peso'), { unidade: 'kg' }),
  linha('Estatura', colunas(avs, 'antropometria', 'altura'), { unidade: 'cm', casas: 0 }),
  linha('IMC', colunas(avs, 'antropometria', 'imc'), { unidade: 'kg/m²', sentido: 'menor', casas: 2 }),
  linha('Relação Cintura-Quadril (IRCQ)', colunas(avs, 'antropometria', 'IRCQ'), { sentido: 'menor', casas: 2 }),
  linha('Índice Cintura-Altura (ICA)', colunas(avs, 'antropometria', 'ICA'), { sentido: 'menor', casas: 2 }),
  linha('Índice de Adiposidade (IAC)', colunas(avs, 'antropometria', 'IAC'), { sentido: 'menor', casas: 2 }),
  linha('Índice de Conicidade', colunas(avs, 'antropometria', 'conicidade'), { sentido: 'menor', casas: 2 }),
  linha('Taxa Metabólica Basal (TMB)', colunas(avs, 'antropometria', 'TMB'), { unidade: 'kcal', casas: 0 }),
  linha('Gasto Energético Total (GET)', colunas(avs, 'antropometria', 'GET'), { unidade: 'kcal', casas: 0 }),
];

const linhasPerimetrosCmp = (avs) => PERIMETROS_LABELS.map(([label, chave]) => linha(label, colunas(avs, 'antropometria', chave), { unidade: 'cm' }));

const linhasComposicaoCmp = (avs) => {
  const gc = avs.map((e) => numCmp(e?.composicaoCorporal?.percentualGordura));
  const peso = avs.map((e) => numCmp(e?.antropometria?.peso));
  const gorda = gc.map((g, i) => (g !== null && peso[i] !== null ? (peso[i] * g) / 100 : null));
  const magra = gc.map((g, i) => (g !== null && peso[i] !== null ? peso[i] - (peso[i] * g) / 100 : null));
  return [
    linha('Percentual de gordura', gc, { unidade: '%', sentido: 'menor' }),
    linha('Massa gorda', gorda, { unidade: 'kg', sentido: 'menor', casas: 2 }),
    linha('Massa isenta de gordura', magra, { unidade: 'kg', sentido: 'maior', casas: 2 }),
    linha('Percentual de massa muscular', colunas(avs, 'composicaoCorporal', 'percentualMassaMuscular'), { unidade: '%', sentido: 'maior' }),
    linha('Percentual de massa óssea', colunas(avs, 'composicaoCorporal', 'percentualMassaOssea'), { unidade: '%' }),
    linha('Percentual de peso residual', colunas(avs, 'composicaoCorporal', 'percentualPesoResidual'), { unidade: '%' }),
  ];
};

const linhasVo2Cmp = (avs) => {
  const rel = avs.map((e) => numCmp(e?.vo2?.vo2));
  const peso = avs.map((e) => numCmp(e?.antropometria?.peso));
  return [
    linha('VO2 máx relativo', rel, { unidade: 'ml/kg/min', sentido: 'maior' }),
    linha('VO2 máx absoluto', rel.map((r, i) => (r !== null && peso[i] !== null ? (r * peso[i]) / 1000 : null)), { unidade: 'L/min', sentido: 'maior', casas: 2 }),
    linha('Capacidade metabólica', rel.map((r) => (r !== null ? r / 3.5 : null)), { unidade: 'METs', sentido: 'maior' }),
    linha('FC máxima', colunas(avs, 'vo2', 'fcmax'), { unidade: 'bpm', casas: 0 }),
    linha('FC de repouso', colunas(avs, 'vo2', 'fcRepouso'), { unidade: 'bpm', sentido: 'menor', casas: 0 }),
  ];
};

const exerciciosNeuro = (d) => {
  const doResultado = d?.resultado?.resultadosExercicios;
  const lista = Array.isArray(doResultado) && doResultado.length
    ? doResultado
    : (Array.isArray(d?.exerciciosIniciais) ? d.exerciciosIniciais : []);
  return lista.filter((ex) => Number(ex?.carga) > 0 && Number(ex?.repeticoes) > 0);
};

// O teste de preensão pode não ser o último registrado: procura na lista de testes realizados.
const preensaoDe = (neuro) => {
  const lista = Array.isArray(neuro?.testesRealizados) ? neuro.testesRealizados : [];
  const teste = lista.find((t) => t?.metodo === 'ApreensaoManual');
  return teste?.preensaoManual || neuro?.preensaoManual || null;
};

const linhasNeuromuscularCmp = (avs) => {
  const nomeDe = (ex) => String(ex?.nome ?? ex?.id ?? '').trim();
  const listas = avs.map((e) => exerciciosNeuro(e?.avaliacaoNeuromuscular));
  const nomes = [...new Set(listas.flat().map(nomeDe))].filter(Boolean);
  const oneRM = (lista, nome) => numCmp(lista.find((ex) => nomeDe(ex) === nome)?.oneRM);

  return [
    linha('Pontuação total (força relativa)', avs.map((e) => numCmp(e?.avaliacaoNeuromuscular?.resultado?.totalPontos)), { unidade: 'pontos', sentido: 'maior', casas: 0 }),
    linha('Repetições máximas', colunas(avs, 'avaliacaoNeuromuscular', 'numRepeticoes'), { unidade: 'reps', sentido: 'maior', casas: 0 }),
    linha('Preensão manual (mão dominante)', colunas(avs, 'avaliacaoNeuromuscular', 'forcaApreensaoManual'), { unidade: 'kgf', sentido: 'maior' }),
    linha('Preensão manual — mão direita', avs.map((e) => numCmp(preensaoDe(e?.avaliacaoNeuromuscular)?.melhorDireita)), { unidade: 'kgf', sentido: 'maior' }),
    linha('Preensão manual — mão esquerda', avs.map((e) => numCmp(preensaoDe(e?.avaliacaoNeuromuscular)?.melhorEsquerda)), { unidade: 'kgf', sentido: 'maior' }),
    linha('Assimetria entre as mãos', avs.map((e) => numCmp(preensaoDe(e?.avaliacaoNeuromuscular)?.assimetriaPercentual)), { unidade: '%', sentido: 'menor' }),
    ...nomes.map((nome) => linha(`1RM — ${nome}`, listas.map((l) => oneRM(l, nome)), { unidade: 'kg', sentido: 'maior' })),
  ];
};

// Gráficos a partir das linhas: seleciona pelo rótulo.
const graficoDe = (linhas, label, titulo) => {
  const l = linhas.find((x) => x.label === label);
  return l ? { titulo: titulo || label, valores: l.valores, unidade: l.unidade, casas: l.casas } : null;
};

const BlocoTituloComparativo = ({ titulo, aluno, datas, intervalo }) => (
  <View style={styles.blocoAluno}>
    <Text style={styles.tituloTopico}>{titulo}</Text>
    <View style={styles.subHeader}>
      <View style={styles.subHeaderLinha}>
        <Text style={styles.subHeaderCampo}><Text style={styles.subHeaderLabel}>Nome: </Text>{v(aluno?.name)}</Text>
        <Text style={styles.subHeaderCampo}><Text style={styles.subHeaderLabel}>Idade: </Text>{v(aluno?.idade)}</Text>
        <Text style={styles.subHeaderCampo}><Text style={styles.subHeaderLabel}>Sexo: </Text>{formatSexo(aluno?.sexo)}</Text>
      </View>
      <View style={styles.subHeaderLinha}>
        <Text style={styles.subHeaderCampo}><Text style={styles.subHeaderLabel}>Avaliações: </Text>{datas.join('  •  ')}</Text>
        <Text style={styles.subHeaderCampo}><Text style={styles.subHeaderLabel}>Período: </Text>{intervalo || '—'}</Text>
      </View>
    </View>
  </View>
);

const PaginaComparativa = ({ personal, aluno, titulo, datas, intervalo, geradoEm, children }) => (
  <Page size="A4" style={styles.page}>
    <MolduraBorda />
    <Header personal={personal} />
    <BlocoTituloComparativo titulo={titulo} aluno={aluno} datas={datas} intervalo={intervalo} />
    <View style={styles.conteudoSecao}>{children}</View>
    <Footer geradoEm={geradoEm} />
  </Page>
);

// Normaliza a entrada: aceita { avaliacoes: [...] } (2 a 4) ou o formato antigo { avaliacaoA, avaliacaoB }.
const listaDeAvaliacoes = ({ avaliacoes, avaliacaoA, avaliacaoB }) => {
  const lista = Array.isArray(avaliacoes) && avaliacoes.length ? avaliacoes : [avaliacaoA, avaliacaoB];
  return lista
    .filter(Boolean)
    .sort((x, y) => new Date(x.evaluationDate) - new Date(y.evaluationDate))
    .slice(0, 4);
};

export function gerarRelatorioComparativo({ avaliacoes, avaliacaoA, avaliacaoB, aluno, personal }) {
  const avs = listaDeAvaliacoes({ avaliacoes, avaliacaoA, avaliacaoB });
  const geradoEm = new Date();
  const datas = avs.map((e) => formatDate(e.evaluationDate));
  const intervalo = avs.length >= 2 ? intervaloEntreDatas(avs[0].evaluationDate, avs[avs.length - 1].evaluationDate) : null;

  const antropometria = linhasAntropometriaCmp(avs);
  const perimetros = linhasPerimetrosCmp(avs);
  const composicao = linhasComposicaoCmp(avs);
  const vo2 = linhasVo2Cmp(avs);
  const neuromuscular = linhasNeuromuscularCmp(avs);

  const graficosAntropometria = [
    graficoDe(antropometria, 'Peso', 'Peso corporal'),
    graficoDe(antropometria, 'IMC', 'Índice de Massa Corporal'),
    graficoDe(perimetros, 'Cintura'),
    graficoDe(perimetros, 'Quadril'),
    graficoDe(perimetros, 'Abdômen'),
    graficoDe(antropometria, 'Relação Cintura-Quadril (IRCQ)', 'Relação Cintura-Quadril'),
  ].filter(Boolean);
  const graficosComposicao = [
    graficoDe(composicao, 'Percentual de gordura'),
    graficoDe(composicao, 'Massa gorda'),
    graficoDe(composicao, 'Massa isenta de gordura'),
    graficoDe(composicao, 'Percentual de massa muscular'),
  ].filter(Boolean);
  const graficosVo2 = [
    graficoDe(vo2, 'VO2 máx relativo'),
    graficoDe(vo2, 'VO2 máx absoluto'),
    graficoDe(vo2, 'FC máxima'),
    graficoDe(vo2, 'FC de repouso'),
  ].filter(Boolean);
  const graficosNeuro = [
    graficoDe(neuromuscular, 'Pontuação total (força relativa)', 'Pontuação do teste de força'),
    graficoDe(neuromuscular, 'Repetições máximas'),
    graficoDe(neuromuscular, 'Preensão manual (mão dominante)'),
    graficoDe(neuromuscular, 'Preensão manual — mão direita'),
    graficoDe(neuromuscular, 'Preensão manual — mão esquerda'),
    ...neuromuscular.filter((l) => l.label.startsWith('1RM — ')).slice(0, 5).map((l) => graficoDe(neuromuscular, l.label)),
  ].filter(Boolean);

  return (
    <Document>
      <Page size="A4" style={styles.capa}>
        <MolduraBorda />
        <Text style={styles.capaTitulo}>RELATÓRIO DE{'\n'}EVOLUÇÃO FÍSICA</Text>
        <View style={styles.capaLogoArea}>
          {personal?.photo ? <Image src={personal.photo} style={styles.logoCapa} /> : null}
        </View>
        <View style={styles.capaRodape}>
          <View style={styles.capaRodapeEsq}>
            <Text style={styles.capaRodapeNome}>{v(aluno?.name)}</Text>
            <Text style={styles.capaRodapeTxt}>Avaliações: {datas.join(', ')}</Text>
            {intervalo ? <Text style={styles.capaRodapeTxt}>Período: {intervalo}</Text> : null}
          </View>
          <View style={styles.capaRodapeDir}>
            <Text style={styles.capaRodapeNome}>{v(personal?.name)}</Text>
            <Text style={styles.capaRodapeTxt}>{v(personal?.email)}</Text>
            {personal?.cref ? <Text style={styles.capaRodapeTxt}>CREF: {personal.cref}</Text> : null}
          </View>
        </View>
      </Page>

      {(temLinhaPreenchida(antropometria) || temLinhaPreenchida(perimetros)) && (
        <PaginaComparativa personal={personal} aluno={aluno} titulo="Evolução — Antropometria" datas={datas} intervalo={intervalo} geradoEm={geradoEm}>
          <TabelaComparativa linhas={antropometria} datas={datas} />
          <GradeGraficos graficos={graficosAntropometria} datas={datas} />
          {temLinhaPreenchida(perimetros) && (
            <View break>
              <Text style={[styles.subtituloBloco, { marginBottom: 4 }]}>Perímetros corporais</Text>
              <TabelaComparativa linhas={perimetros} datas={datas} />
            </View>
          )}
        </PaginaComparativa>
      )}

      {temLinhaPreenchida(composicao) && (
        <PaginaComparativa personal={personal} aluno={aluno} titulo="Evolução — Composição Corporal" datas={datas} intervalo={intervalo} geradoEm={geradoEm}>
          <TabelaComparativa linhas={composicao} datas={datas} />
          <GradeGraficos graficos={graficosComposicao} datas={datas} />
        </PaginaComparativa>
      )}

      {temLinhaPreenchida(vo2) && (
        <PaginaComparativa personal={personal} aluno={aluno} titulo="Evolução — Capacidade Cardiorrespiratória" datas={datas} intervalo={intervalo} geradoEm={geradoEm}>
          <TabelaComparativa linhas={vo2} datas={datas} />
          <GradeGraficos graficos={graficosVo2} datas={datas} />
        </PaginaComparativa>
      )}

      {temLinhaPreenchida(neuromuscular) && (
        <PaginaComparativa personal={personal} aluno={aluno} titulo="Evolução — Avaliação Neuromuscular" datas={datas} intervalo={intervalo} geradoEm={geradoEm}>
          <TabelaComparativa linhas={neuromuscular} datas={datas} />
          <GradeGraficos graficos={graficosNeuro} datas={datas} />
        </PaginaComparativa>
      )}
    </Document>
  );
}

// Indica se há algo a comparar entre as avaliações (evita PDF só com a capa).
export const temComparativo = (...args) => {
  const entrada = args.length === 1 && (Array.isArray(args[0]) || args[0]?.avaliacoes)
    ? (Array.isArray(args[0]) ? { avaliacoes: args[0] } : args[0])
    : { avaliacaoA: args[0], avaliacaoB: args[1] };
  const avs = listaDeAvaliacoes(entrada);
  if (avs.length < 2) return false;
  return [
    linhasAntropometriaCmp(avs),
    linhasPerimetrosCmp(avs),
    linhasComposicaoCmp(avs),
    linhasVo2Cmp(avs),
    linhasNeuromuscularCmp(avs),
  ].some(temLinhaPreenchida);
};

// ---------- Estilos ----------

const COR_DESTAQUE = '#d11b1b';
const COR_TEXTO = '#1f2937';
const COR_TEXTO_SUAVE = '#4b5563';
const COR_BORDA = '#e5e7eb';
const COR_FUNDO_ALT = '#f9fafb';

const styles = StyleSheet.create({
  page: {
    paddingTop: 142,
    paddingBottom: 60,
    paddingHorizontal: 36,
    fontSize: 10,
    color: COR_TEXTO,
    fontFamily: 'Helvetica',
  },
  capa: {
    padding: 50,
    fontFamily: 'Helvetica',
    color: COR_TEXTO,
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  capaTitulo: {
    fontSize: 28,
    fontWeight: 'bold',
    color: COR_DESTAQUE,
    textAlign: 'center',
    letterSpacing: 1,
    lineHeight: 1.25,
    marginTop: 10,
  },
  capaLogoArea: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoCapa: {
    width: 300,
  },
  capaRodape: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    borderTop: `1pt solid ${COR_BORDA}`,
    paddingTop: 12,
  },
  capaRodapeEsq: {
    flexDirection: 'column',
  },
  capaRodapeDir: {
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  capaRodapeLabel: {
    fontSize: 8,
    color: COR_TEXTO_SUAVE,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 2,
  },
  capaRodapeNome: {
    fontSize: 13,
    fontWeight: 'bold',
    color: COR_TEXTO,
  },
  capaRodapeTxt: {
    fontSize: 9.5,
    color: COR_TEXTO_SUAVE,
  },
  moldura: {
    position: 'absolute',
    top: 14,
    left: 14,
    right: 14,
    bottom: 14,
    border: '1.5pt solid #000000',
    borderRadius: 3,
  },
  header: {
    position: 'absolute',
    top: 24,
    left: 30,
    right: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 8,
    borderBottom: `1pt solid ${COR_BORDA}`,
  },
  headerInfo: {
    flexDirection: 'column',
    justifyContent: 'center',
  },
  logoHeader: {
    width: 100,
    height: 100,
    objectFit: 'cover',
    borderRadius: 50,
  },
  personalNome: {
    fontSize: 14,
    fontWeight: 'bold',
    color: COR_TEXTO,
    marginBottom: 2,
  },
  personalContato: {
    fontSize: 9.5,
    color: COR_TEXTO_SUAVE,
  },
  blocoAluno: {
    marginBottom: 12,
  },
  tituloTopico: {
    fontSize: 12,
    fontWeight: 'bold',
    color: COR_TEXTO,
    backgroundColor: '#e5e7eb',
    border: '1pt solid #9ca3af',
    paddingVertical: 5,
    paddingHorizontal: 8,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  subHeader: {
    backgroundColor: COR_FUNDO_ALT,
    borderLeft: '1pt solid #9ca3af',
    borderRight: '1pt solid #9ca3af',
    borderBottom: '1pt solid #9ca3af',
    padding: 8,
  },
  subHeaderLinha: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  subHeaderCampo: {
    fontSize: 9,
    color: COR_TEXTO,
    flex: 1,
  },
  subHeaderLabel: {
    fontWeight: 'bold',
  },
  conteudoSecao: {
    marginTop: 4,
  },
  tabela: {
    borderTop: `1pt solid ${COR_BORDA}`,
    borderLeft: `1pt solid ${COR_BORDA}`,
    borderRight: `1pt solid ${COR_BORDA}`,
    marginBottom: 8,
  },
  linhaTabela: {
    flexDirection: 'row',
    borderBottom: `1pt solid ${COR_BORDA}`,
  },
  linhaTabelaAlt: {
    backgroundColor: COR_FUNDO_ALT,
  },
  celulaLabel: {
    flex: 1,
    padding: 6,
    fontSize: 9,
    fontWeight: 'bold',
    color: COR_TEXTO,
  },
  celulaValor: {
    flex: 1,
    padding: 6,
    fontSize: 9,
    color: COR_TEXTO,
  },
  linhaTabelaHead: {
    backgroundColor: '#e5e7eb',
  },
  celIndiceLabel: {
    flex: 2,
    padding: 6,
    fontSize: 9,
    fontWeight: 'bold',
    color: COR_TEXTO,
  },
  celIndiceValor: {
    flex: 1,
    padding: 6,
    fontSize: 9,
    color: COR_TEXTO,
  },
  celIndiceClasse: {
    flex: 2,
    padding: 6,
    fontSize: 9,
    color: COR_TEXTO,
  },
  celExerc: {
    flex: 2,
    padding: 5,
    fontSize: 8.5,
    color: COR_TEXTO,
  },
  celNum: {
    flex: 1,
    padding: 5,
    fontSize: 8.5,
    color: COR_TEXTO,
    textAlign: 'right',
  },
  anamneseGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  itemAnamnese: {
    width: '50%',
    paddingRight: 10,
    marginBottom: 8,
  },
  perguntaAnamnese: {
    fontSize: 10.5,
    fontWeight: 'bold',
    color: COR_TEXTO,
    marginBottom: 2,
  },
  respostaAnamnese: {
    fontSize: 10.5,
    color: COR_TEXTO_SUAVE,
  },
  anamneseObs: {
    marginTop: 10,
    border: `1pt solid ${COR_BORDA}`,
    borderRadius: 4,
    padding: 8,
    backgroundColor: COR_FUNDO_ALT,
  },
  anamneseObsTitulo: {
    fontSize: 11,
    fontWeight: 'bold',
    color: COR_TEXTO,
    marginBottom: 4,
  },
  anamneseObsTexto: {
    fontSize: 10.5,
    color: COR_TEXTO,
    lineHeight: 1.4,
    textAlign: 'justify',
  },
  duasColunas: {
    flexDirection: 'row',
  },
  coluna: {
    flex: 1,
  },
  colunaEsq: {
    marginRight: 8,
  },
  graficoBarras: {
    marginTop: 8,
  },
  barraLinha: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 3,
  },
  barraLabel: {
    width: 95,
    fontSize: 8,
    color: COR_TEXTO,
  },
  barraTrack: {
    flex: 1,
    height: 8,
    backgroundColor: '#eef0f3',
    borderRadius: 2,
    marginHorizontal: 4,
  },
  barraFill: {
    height: 8,
    backgroundColor: COR_DESTAQUE,
    borderRadius: 2,
  },
  barraValor: {
    width: 44,
    fontSize: 8,
    textAlign: 'right',
    color: COR_TEXTO_SUAVE,
  },
  barraTituloIndice: {
    fontSize: 9,
    fontWeight: 'bold',
    color: COR_TEXTO,
    marginTop: 8,
    marginBottom: 2,
  },
  barraFaixasWrap: {
    marginBottom: 4,
  },
  barraFaixasArea: {
    position: 'relative',
    paddingTop: 2,
  },
  barraFaixasTrack: {
    flexDirection: 'row',
    height: 10,
    borderRadius: 2,
  },
  barraFaixasTick: {
    position: 'absolute',
    top: 0,
    width: 2,
    height: 14,
    backgroundColor: '#111827',
    zIndex: 2,
  },
  barraFaixasLegenda: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 5,
  },
  barraFaixasLegItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 10,
    marginBottom: 2,
  },
  barraFaixasLegCor: {
    width: 7,
    height: 7,
    borderRadius: 1,
    marginRight: 3,
  },
  barraFaixasLegTxt: {
    fontSize: 7.5,
    color: COR_TEXTO_SUAVE,
  },
  barraFaixasLegAtual: {
    fontWeight: 'bold',
    color: COR_TEXTO,
  },
  coronarioPergunta: {
    flex: 2,
    padding: 5,
    fontSize: 8.5,
    fontWeight: 'bold',
    color: COR_TEXTO,
  },
  coronarioResposta: {
    flex: 5,
    padding: 5,
    fontSize: 8,
    color: COR_TEXTO,
  },
  coronarioPainel: {
    marginTop: 10,
    border: '1.5pt solid #d1d5db',
    borderRadius: 6,
    padding: 10,
    backgroundColor: '#fafafa',
  },
  coronarioPainelTopo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  coronarioPontos: {
    fontSize: 22,
    fontWeight: 'bold',
    color: COR_TEXTO,
  },
  coronarioPontosLabel: {
    fontSize: 11,
    fontWeight: 'normal',
    color: COR_TEXTO_SUAVE,
  },
  coronarioBadge: {
    paddingVertical: 5,
    paddingHorizontal: 12,
    borderRadius: 12,
  },
  coronarioBadgeTxt: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  coronarioNota: {
    marginTop: 12,
  },
  coronarioNotaTxt: {
    fontSize: 8,
    color: COR_TEXTO_SUAVE,
    textAlign: 'justify',
    marginBottom: 4,
  },
  cardIndicador: {
    border: `1pt solid ${COR_BORDA}`,
    borderRadius: 5,
    padding: 8,
    marginBottom: 8,
  },
  cardIndicadorTitulo: {
    fontSize: 10,
    fontWeight: 'bold',
    color: COR_TEXTO,
    marginBottom: 4,
  },
  cardIndicadorLinha: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 4,
  },
  cardIndicadorValor: {
    fontSize: 13,
    fontWeight: 'bold',
    color: COR_DESTAQUE,
  },
  cardIndicadorClasse: {
    fontSize: 9,
    fontWeight: 'bold',
    color: COR_TEXTO,
  },
  cardIndicadorNota: {
    fontSize: 8,
    color: COR_TEXTO_SUAVE,
    marginTop: 2,
    marginBottom: 2,
  },
  compResultadoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  compLegenda: {
    flex: 1,
    marginLeft: 18,
  },
  compLegItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5,
  },
  compLegCor: {
    width: 9,
    height: 9,
    borderRadius: 2,
    marginRight: 6,
  },
  compLegTxt: {
    fontSize: 9.5,
    color: COR_TEXTO,
  },
  celNum3: {
    flex: 1,
    padding: 5,
    fontSize: 9,
    color: COR_TEXTO,
    textAlign: 'center',
  },
  celTesteCF: {
    flex: 2.4,
    padding: 5,
    fontSize: 9,
    color: COR_TEXTO,
  },
  indicadorTopo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  indicadorTitulo: {
    fontSize: 10,
    fontWeight: 'bold',
    color: COR_TEXTO,
    flexShrink: 1,
    paddingRight: 8,
  },
  indicadorValor: {
    fontSize: 11,
    fontWeight: 'bold',
    color: COR_DESTAQUE,
  },
  indicadorSelo: {
    paddingVertical: 3,
    paddingHorizontal: 10,
    borderRadius: 10,
  },
  indicadorSeloTxt: {
    fontSize: 8.5,
    fontWeight: 'bold',
  },
  parqTermo: {
    marginTop: 12,
    border: `1pt solid ${COR_BORDA}`,
    borderRadius: 4,
    padding: 8,
    backgroundColor: COR_FUNDO_ALT,
  },
  parqTermoTitulo: {
    fontSize: 10,
    fontWeight: 'bold',
    color: COR_TEXTO,
    marginBottom: 4,
  },
  parqTermoTexto: {
    fontSize: 9,
    color: COR_TEXTO,
    lineHeight: 1.4,
    textAlign: 'justify',
  },
  perimetrosLayout: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  perimetrosLegenda: {
    flex: 1,
    marginLeft: 16,
  },
  legendaPerimetroItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  legendaPerimetroCor: {
    width: 9,
    height: 9,
    borderRadius: 5,
    marginRight: 6,
  },
  legendaPerimetroTxt: {
    fontSize: 8.5,
    color: COR_TEXTO,
  },
  graficoLinhaLinha: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  graficoLinhaCelula: {
    flex: 1,
  },
  graficoLinhaCaixa: {
    border: `1pt solid ${COR_BORDA}`,
    borderRadius: 5,
    padding: 6,
  },
  graficoLinhaTitulo: {
    fontSize: 8.5,
    fontWeight: 'bold',
    color: COR_TEXTO,
    marginBottom: 2,
  },
  celCompLabel: {
    flex: 2.6,
    padding: 5,
    fontSize: 8.5,
    color: COR_TEXTO,
  },
  celCompValor: {
    flex: 1.2,
    padding: 4,
    fontSize: 8,
    color: COR_TEXTO,
    textAlign: 'center',
  },
  celCompVar: {
    flex: 1.6,
    padding: 4,
    fontSize: 8,
    color: COR_TEXTO,
    textAlign: 'center',
  },
  celTesteCFClasse: {
    flex: 1.4,
    padding: 5,
    fontSize: 9,
    color: COR_TEXTO,
    textAlign: 'center',
  },
  vo2CardTopo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  vo2CardTitulo: {
    fontSize: 10.5,
    fontWeight: 'bold',
    color: COR_TEXTO,
    flexShrink: 1,
    paddingRight: 8,
  },
  vo2CardSub: {
    fontSize: 8,
    color: COR_TEXTO_SUAVE,
    marginBottom: 6,
  },
  vo2Badge: {
    paddingVertical: 3,
    paddingHorizontal: 10,
    borderRadius: 10,
  },
  vo2BadgeTxt: {
    fontSize: 9,
    fontWeight: 'bold',
  },
  vo2StatsRow: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  vo2Stat: {
    flex: 1,
    border: `1pt solid ${COR_BORDA}`,
    borderRadius: 4,
    padding: 6,
  },
  vo2StatEsq: {
    marginRight: 6,
  },
  vo2StatFantasma: {
    flex: 1,
    marginLeft: 6,
  },
  vo2StatLabel: {
    fontSize: 7.5,
    color: COR_TEXTO_SUAVE,
    marginBottom: 2,
  },
  vo2StatValor: {
    fontSize: 12,
    fontWeight: 'bold',
    color: COR_DESTAQUE,
  },
  vo2StatUnidade: {
    fontSize: 8,
    fontWeight: 'normal',
    color: COR_TEXTO_SUAVE,
  },
  vo2SubBloco: {
    marginTop: 8,
  },
  vo2LimiaresRow: {
    flexDirection: 'row',
    marginTop: 4,
  },
  vo2LimiarCol: {
    flex: 1,
    border: `1pt solid ${COR_BORDA}`,
    borderRadius: 4,
    padding: 6,
    backgroundColor: COR_FUNDO_ALT,
  },
  vo2LimiarTitulo: {
    fontSize: 9,
    fontWeight: 'bold',
    color: COR_TEXTO,
    marginBottom: 3,
  },
  vo2LimiarLinha: {
    fontSize: 8.5,
    color: COR_TEXTO_SUAVE,
    marginBottom: 1.5,
  },
  vo2LimiarValor: {
    fontWeight: 'bold',
    color: COR_TEXTO,
  },
  ptaGrade: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  ptaPostit: {
    width: '49%',
    border: `1pt solid ${COR_BORDA}`,
    borderLeft: `3pt solid ${COR_DESTAQUE}`,
    borderRadius: 4,
    padding: 8,
    marginBottom: 8,
    backgroundColor: COR_FUNDO_ALT,
  },
  ptaPostitTitulo: {
    fontSize: 9.5,
    fontWeight: 'bold',
    color: COR_TEXTO,
    marginBottom: 3,
  },
  ptaPostitDesc: {
    fontSize: 8.5,
    color: COR_TEXTO,
    lineHeight: 1.4,
  },
  ptaPostitDayoff: {
    fontSize: 8.5,
    fontWeight: 'bold',
    color: COR_TEXTO_SUAVE,
  },
  ptaCardsRow: {
    flexDirection: 'row',
  },
  ptaCard: {
    flex: 1,
    border: `1pt solid ${COR_BORDA}`,
    borderRadius: 5,
    padding: 8,
    marginBottom: 8,
  },
  ptaCardEsq: {
    marginRight: 8,
  },
  // Card de largura inteira (fora de linha flex): sem flex pra não colapsar a altura.
  ptaCardCheio: {
    border: `1pt solid ${COR_BORDA}`,
    borderRadius: 5,
    padding: 8,
    marginBottom: 8,
  },
  ptaCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5,
  },
  ptaCardTitulo: {
    fontSize: 9,
    fontWeight: 'bold',
    color: COR_TEXTO,
    marginLeft: 4,
  },
  ptaLinhaDado: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  ptaLinhaLabel: {
    fontSize: 8.5,
    color: COR_TEXTO_SUAVE,
  },
  ptaLinhaValor: {
    fontSize: 9,
    fontWeight: 'bold',
    color: COR_TEXTO,
  },
  linhaParq: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
    borderBottom: `0.5pt solid ${COR_BORDA}`,
  },
  perguntaParq: {
    fontSize: 11,
    flex: 4,
  },
  respostaParq: {
    fontSize: 11,
    flex: 1,
    textAlign: 'right',
    fontWeight: 'bold',
  },
  parqResponsabilidade: {
    fontSize: 9,
    fontWeight: 'bold',
    color: COR_TEXTO,
    marginTop: 10,
  },
  subtituloBloco: {
    fontSize: 10,
    fontWeight: 'bold',
    color: COR_TEXTO,
    marginTop: 8,
    marginBottom: 4,
  },
  blocoTreino: {
    padding: 6,
    backgroundColor: COR_FUNDO_ALT,
    marginBottom: 4,
    borderRadius: 3,
  },
  tituloTreino: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  descricaoTreino: {
    fontSize: 9,
    color: COR_TEXTO_SUAVE,
  },
  notaRodape: {
    fontSize: 8,
    color: COR_TEXTO_SUAVE,
    marginTop: 12,
    fontStyle: 'italic',
  },
  footer: {
    position: 'absolute',
    bottom: 22,
    left: 30,
    right: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 6,
    borderTop: `0.5pt solid ${COR_BORDA}`,
  },
  footerEsq: {
    fontSize: 8,
    color: COR_TEXTO_SUAVE,
  },
  footerDir: {
    fontSize: 8,
    color: COR_TEXTO_SUAVE,
  },
});
